import type { Dicionario } from './tipos'

/**
 * English.
 *
 * Tipado como `Dicionario`: uma chave a menos ou a mais não compila. Os
 * parâmetros (`{nome}`) têm de ser os mesmos do pt-BR — o teste de paridade
 * confere isso, porque o tipo só garante as chaves.
 *
 * Vocabulário de sinuca, para não traduzir ao pé da letra:
 *   recuo = draw · corrida = follow · efeito lateral = english / side spin
 *   caçapa = pocket · tabela = cushion / rail · bola na mão = ball in hand
 *   quebra = break · falta = foul · branca = cue ball · tacada = shot
 */
export const en: Dicionario = {
  // ------------------------------------------------------------------ comum
  'comum.verNaChain': 'view on-chain ↗',
  'comum.lendoBlockchain': 'Reading from the blockchain…',
  'comum.carregando': 'Loading…',
  'comum.aguardando': 'waiting…',

  // ----------------------------------------------------------------- idioma
  'idioma.rotulo': 'Language',
  'idioma.pt-BR': 'Português',
  'idioma.en': 'English',

  // --------------------------------------------------------- erros locais
  'erro.recusouSolicitacao': 'You declined the request in your wallet.',
  'erro.semCarteira': 'No Solana wallet found in this browser.',
  'erro.inesperado': 'Unexpected error.',
  'erro.saldoInsuficiente':
    'Insufficient balance: the transaction needs {precisa} SOL and the wallet has {tem}. Nothing was charged.',
  'erro.recusouAssinatura': 'You declined to sign in your wallet. Nothing was sent.',
  'erro.expirou': 'The transaction took too long and expired before landing in a block. Try again.',
  'erro.limiteRpc':
    'The network refused the call due to momentary overuse. Wait a few seconds and try again — nothing was sent.',
  'erro.servidorNaoRespondeu': 'The server did not respond in time.',
  'erro.transacaoFalhou': 'The transaction reached the network and failed. Nothing was charged — try again.',

  // ------------------------------------------- classe de erro do servidor
  'erro.codigo.unauthenticated': 'Sign in before using tables.',
  'erro.codigo.bad_request': 'The server did not understand the message.',
  'erro.codigo.nonce_invalid': 'Your login session is no longer valid. Sign in again.',
  'erro.codigo.signature_invalid': 'The login signature does not match.',
  'erro.codigo.stake_out_of_range': 'Stake amount out of range.',
  'erro.codigo.deposit_not_found': 'Deposit not found on the blockchain.',
  'erro.codigo.deposit_mismatch': 'The on-chain deposit does not match the table.',
  'erro.codigo.room_not_found': 'Table not found.',
  'erro.codigo.room_not_joinable': 'This table no longer accepts players.',
  'erro.codigo.already_in_room': 'You are already at a table.',
  'erro.codigo.not_room_creator': 'Only the creator can cancel.',
  'erro.codigo.no_reservation': 'No reservation to confirm.',
  'erro.codigo.chain_error': 'Failed to read the blockchain.',
  'erro.codigo.rate_limited': 'Too many messages in a short time. Wait a moment.',
  'erro.codigo.voice_off': 'This table has voice turned off.',
  'erro.codigo.voice_not_allowed': 'You are not at this table.',
  'erro.codigo.voice_unavailable': 'Voice is unavailable right now.',
  'erro.codigo.internal': 'Internal error.',
  'erro.codigo.engine_unverified':
    'This browser engine has not passed the determinism check yet. Wagering is closed on it.',
  'erro.codigo.match_not_found': 'Match not found.',
  'erro.codigo.not_your_turn': 'It is not your turn.',
  'erro.codigo.wrong_phase': 'The match does not accept that right now.',
  'erro.codigo.bad_shot': 'Invalid shot.',
  'erro.codigo.bad_decision': 'Invalid choice.',
  'erro.codigo.unknown_player': 'This wallet is not in this match.',
  'erro.codigo.already_revealed': 'You already did that in this match.',
  'erro.codigo.bad_reveal': 'The nonce does not match the commitment sent.',
  'erro.codigo.commit_mismatch': "This match's commitment is not what is on the blockchain.",
  'erro.codigo.spectators_disabled': 'This table does not allow spectators.',
  'erro.codigo.chat_too_long': 'The message is too long.',
  'erro.codigo.chat_too_fast': 'Wait a moment before sending another.',
  'erro.codigo.not_in_match': 'You are not at any table.',
  'erro.codigo.evento_nao_encontrado': 'Event not found.',
  'erro.codigo.evento_cheio': 'This event is already full.',
  'erro.codigo.evento_fechado': 'This event has already started.',
  'erro.codigo.ja_em_evento': 'You are already in an event.',
  'erro.codigo.fora_do_evento': 'You are not in any event.',
  'erro.codigo.formato_invalido': 'Unknown table format.',
  'erro.codigo.em_manutencao': 'The server is about to be updated: tables cannot be created or joined right now.',
  'erro.codigo.password_required': 'This table has a password.',
  'erro.codigo.password_incorrect': 'Wrong password.',
  'erro.codigo.password_invalid': 'The password is outside the allowed length.',
  'erro.codigo.password_attempts': 'Too many password attempts. Wait a moment.',
  'erro.codigo.invite_not_found': 'No table with this code.',
  'erro.codigo.invite_invalid': 'Invalid invite code.',
  'erro.codigo.invite_attempts': 'Too many codes in a short time. Wait a moment.',
  'erro.codigo.nickname_invalid': 'Nickname outside the allowed format.',
  'erro.codigo.nickname_forbidden': 'This nickname is not allowed.',
  'erro.codigo.nickname_attempts': 'Too many nickname changes. Wait a moment.',

  // --------------------------------------------------- eventos do servidor
  'evento.auth.falhou': 'Authentication failed.',
  'evento.auth.necessaria': 'Sign in before using tables.',
  'evento.auth.endereco_invalido': 'Invalid wallet address.',
  'evento.auth.nonce_desconhecido': 'Unknown or already used nonce.',
  'evento.auth.nonce_expirado': 'Nonce expired.',
  'evento.auth.nonce_de_outra_carteira': 'Nonce issued for another wallet.',
  'evento.auth.mensagem_nao_confere': 'The signed message does not match.',
  'evento.auth.assinatura_mal_formada': 'Malformed signature.',
  'evento.auth.assinatura_invalida': 'Invalid signature.',
  'evento.auth.sessao_desconhecida': 'Unknown session.',
  'evento.auth.sessao_expirada': 'Session expired.',

  'evento.geral.interno': 'Internal error.',
  'evento.geral.mensagem_invalida': 'Invalid message.',

  'evento.motor.webkit_nao_verificado':
    'Physics determinism has not been verified on WebKit yet (Safari, and every browser on iPhone). Until it is, matches for SOL stay closed — playing without a stake is still open.',
  'evento.motor.desconhecido':
    'We could not identify this browser engine, and only engines that passed the determinism check are allowed. Use Firefox or Chrome to wager.',

  'evento.lobby.entrada_fora_dos_limites': 'Stake amount out of range.',
  'evento.lobby.ja_em_sala': 'You are already at a table.',
  'evento.lobby.sem_reserva_de_mesa': 'No table reservation to confirm.',
  'evento.lobby.reserva_vencida': 'The reservation expired. Start again — the deposit comes back on the blockchain deadline.',
  'evento.lobby.sem_reserva_de_entrada': 'No join reservation to confirm.',
  'evento.lobby.deposito_nao_encontrado': 'Deposit not found on the blockchain.',
  'evento.lobby.deposito_de_outra_carteira': 'The deposit was made by another wallet.',
  'evento.lobby.deposito_valor_diferente': 'The deposited amount does not match the reservation.',
  'evento.lobby.prazo_curto_demais':
    "This match's deadline is too short for a full match; cancel and create the table again.",
  'evento.lobby.manutencao':
    'The server is about to be updated: tables cannot be created or joined right now. Matches in progress will finish.',
  'manutencao.faixa':
    'Update coming shortly: tables cannot be created or joined right now. Matches in progress will finish.',
  'manutencao.faixaComHora':
    'Update scheduled for {hora}: tables cannot be created or joined right now. Matches in progress will finish.',
  'evento.lobby.sala_nao_encontrada': 'Table not found.',
  'evento.lobby.sala_cheia': 'This table no longer accepts players.',
  'evento.eventomesa.nao_encontrado': 'Event not found.',
  'evento.eventomesa.cheio': 'This event is already full.',
  'evento.eventomesa.fechado': 'This event has already started.',
  'evento.eventomesa.ja_em_evento': 'You are already in an event.',
  'evento.eventomesa.fora': 'You are not in any event.',
  'evento.eventomesa.formato_invalido': 'Unknown table format.',
  'evento.eventomesa.entrada_invalida': 'Invalid entry fee.',
  'evento.eventomesa.inscricao_nao_paga': 'The entry fee has not been paid yet.',
  'evento.eventomesa.pago_indisponivel': 'Paid tournaments are not available yet.',
  'evento.eventomesa.id_em_uso': 'This tournament already exists.',
  'evento.eventomesa.nao_e_o_dono': 'Only the tournament creator can cancel it.',
  'evento.eventomesa.ocupado_em_mesa': 'Finish or cancel your table before joining an event.',
  'evento.eventomesa.sair_depois_de_comecar': 'The event has started: leaving now loses on the clock.',
  'evento.lobby.propria_mesa': 'You cannot join your own table.',
  'evento.lobby.prazo_vencido': "This table's deadline has passed on the blockchain.",
  'evento.lobby.partida_nao_encontrada_na_chain': 'Match not found on the blockchain.',
  'evento.lobby.deposito_do_oponente_pendente': "The opponent's deposit has not confirmed yet.",
  'evento.lobby.entrou_outra_carteira': 'A different wallet joined the match.',
  'evento.lobby.so_criador_cancela': 'Only the creator can cancel.',
  'evento.lobby.mesa_ja_comprometida': 'The table is already committed.',
  'evento.lobby.partida_ainda_na_chain': 'The match still exists on the blockchain.',
  'evento.lobby.falha_ao_ler_chain': 'Failed to read the blockchain: {erro}',
  'evento.lobby.senha_obrigatoria': 'This table has a password. Ask whoever created it.',
  'evento.lobby.senha_incorreta': 'Wrong password.',
  'evento.lobby.senha_invalida': 'The password must be {min} to {max} characters long.',
  'evento.lobby.senha_muitas_tentativas':
    'Too many attempts at this table. Wait {segundos} s before trying again.',
  'evento.lobby.codigo_nao_encontrado': 'No open table with this code. Check with whoever invited you.',
  'evento.lobby.codigo_invalido': 'Invalid code: it has 6 characters, with no 0, O, 1 or I.',
  'evento.lobby.codigo_muitas_tentativas': 'Too many codes in a short time. Wait {segundos} s.',
  'evento.perfil.apelido_invalido':
    'The nickname must be {min} to {max} characters: letters, digits, space, _ or -.',
  'evento.perfil.apelido_proibido': 'This nickname is not allowed. Pick another.',
  'evento.perfil.muitas_trocas': 'Too many nickname changes. Wait {segundos} s.',

  'evento.partida.nenhuma': 'You are not in any match.',
  'evento.partida.ja_em_partida': 'You are already in another match.',
  'evento.partida.ja_comprometido': 'You have already committed.',
  'evento.partida.quebra_ja_definida': 'The break has already been set.',
  'evento.partida.ja_revelou': 'This player has already revealed.',
  'evento.partida.nonce_nao_confere': 'The nonce does not match the commitment sent.',
  'evento.partida.compromisso_diverge_da_chain':
    "This match's commitment is not what is on the blockchain. This usually happens when you deposit in one browser and play in another, or after clearing site data. Open the match in the same browser you deposited from.",
  'evento.partida.nao_espera_tacada': 'The match is not waiting for a shot.',
  'evento.partida.nao_e_sua_vez': 'It is not your turn.',
  'evento.partida.coloque_a_branca': 'Place the cue ball before shooting.',
  'evento.partida.limite_gravavel': 'The match reached the recording limit.',
  'evento.partida.declare_a_cacapa': 'Call the pocket before shooting.',
  'evento.partida.sem_bola_na_mao': 'There is no ball in hand right now.',
  'evento.partida.bola_na_mao_nao_e_sua': 'The ball in hand is not yours.',
  'evento.partida.posicao_invalida': 'Invalid position.',
  'evento.partida.branca_atras_da_linha':
    'Foul on the break: the cue ball goes behind the head string.',
  'evento.partida.sem_decisao_pendente': 'There is no pending decision.',
  'evento.partida.escolha_nao_e_sua': 'The choice is not yours.',
  'evento.partida.opcao_inexistente': 'Option {opcao} does not exist.',
  'evento.partida.ainda_nao_comecou':
    'The match has not started yet; reveal your nonce or wait for the reveal deadline.',
  'evento.partida.carteira_fora_da_partida': 'This wallet is not in this match.',
  'evento.partida.angulo_fora_da_faixa': 'Angle out of range: {valor}.',
  'evento.partida.forca_fora_da_faixa': 'Power out of range: {valor}.',
  'evento.partida.efeito_fora_da_faixa': 'Spin out of range on {eixo}: {valor}.',
  'evento.partida.efeito_alem_do_limite': 'Spin beyond the miscue limit: {valor}.',

  'evento.liquidacao.sem_chave': 'no settlement key; stakes are refunded at the deadline',
  'evento.liquidacao.sem_revelacao': 'match without reveal; it will be refunded',
  'evento.liquidacao.sem_vencedor': 'match without a winner; it will be refunded',
  'evento.liquidacao.id_invalido': 'invalid match id: {id}',
  'evento.liquidacao.carteira_invalida': 'invalid wallet in the match',
  'evento.liquidacao.ja_liquidada': 'it was already settled',
  'evento.liquidacao.falha_na_chain': 'the blockchain rejected the settlement: {erro}',
  'evento.liquidacao.inalcancavel': 'the blockchain did not respond; the sweeper will retry',

  'evento.faucet.autentique': 'Authenticate your wallet before requesting test SOL.',
  'evento.faucet.so_devnet': 'The faucet only exists on devnet.',
  'evento.faucet.endereco_invalido': 'Invalid address.',
  'evento.faucet.aguarde': 'Wait {segundos}s before requesting again.',
  'evento.faucet.limite_da_rede':
    'This network has requested too much test SOL. Try again in {segundos}s.',
  'evento.faucet.airdrop_no_limite':
    "The network airdrop is at its limit and there is no faucet wallet configured.",

  // History (`/api/history`).
  'evento.historico.endereco_invalido': 'The requested address is not a Solana key.',
  'evento.historico.sem_indice':
    'The server has no history index; reading straight from the blockchain, which is slower.',
  'evento.historico.aguarde': 'Wait {segundos}s before requesting another history refresh.',
  'evento.faucet.sem_saldo': 'The faucet wallet is out of funds.',
  'evento.faucet.falha_ao_enviar': 'Failed to send: {erro}',

  // Public taco mint. Event domain is `mint`, not `tacos.mint`: see the
  // comment in `packages/protocol/src/eventos.ts`.
  'evento.mint.ja_tem': 'This wallet already has a taco from the collection.',
  'evento.mint.esgotado': 'The test tacos ran out.',
  'evento.mint.pagamento_invalido':
    "The payment on the network doesn't match the price, the destination or the wallet.",
  'evento.mint.pagamento_nao_encontrado':
    "I still can't find the payment on the network. Wait a few seconds and try again — you won't be charged twice.",
  'evento.mint.pagamento_usado': 'That payment was already used.',
  'evento.mint.sem_pagamento': 'Payment is missing.',
  'evento.mint.indisponivel': 'Minting is turned off on this server.',
  'evento.mint.rpc': 'The network failed to mint. Your payment is safe — try again.',
  'evento.mint.rapido_demais': 'Too many mint requests. Wait a moment.',

  // Taco upgrade. Event domain is `upgrade`, a single dot, like `mint`.
  'evento.upgrade.nao_eh_seu': 'That taco does not belong to this wallet.',
  'evento.upgrade.gratuito': 'Free tacos do not level up.',
  'evento.upgrade.orcamento': 'This taco already spent every level its rarity allows.',
  'evento.upgrade.nivel_maximo': 'This attribute is already at its maximum level.',
  'evento.upgrade.teto_do_atributo': 'This attribute is already at its maximum points.',
  'evento.upgrade.pagamento_invalido':
    "The payment on the network doesn't match the price, the destination or the wallet.",
  'evento.upgrade.pagamento_usado': 'That payment was already used.',
  'evento.upgrade.pagamento_nao_encontrado':
    "I still can't find the payment on the network. Wait a few seconds and try again — you won't be charged twice.",
  'evento.upgrade.sem_pagamento': 'Payment is missing.',
  'evento.upgrade.atributo_invalido': 'Unknown attribute.',
  'evento.upgrade.indisponivel': 'Taco upgrades are turned off on this server.',
  'evento.upgrade.rpc': 'The network failed to apply the level. Your payment is safe — try again.',
  'evento.upgrade.rapido_demais': 'Too many upgrade requests. Wait a moment.',

  'evento.espectador.sala_nao_permite':
    'This table does not allow spectators — its creator decided so.',
  'evento.espectador.partida_ainda_nao_comecou':
    "This table's match has not started yet. Come back once it closes.",
  'evento.espectador.joga_nesta_mesa': 'You play at this table; enter it as a player, not as a spectator.',

  'evento.chat.vazia': 'Empty message.',
  'evento.chat.longa_demais': 'The message exceeds {maximo} characters.',
  'evento.chat.rapido_demais': 'Wait a moment before sending another.',
  'evento.chat.fora_da_mesa': 'Only people at the table — playing or watching — can chat there.',
  'lobby.permitirVoz': 'Table voice',
  'criar.vozExplica': 'Talk over voice during the match.',
  'criar.resumo.voz': 'Voice',
  'criar.resumo.vozSim': 'on',
  'criar.resumo.vozNao': 'off',
  'taco.selecionado': 'Selected cue',
  'taco.apostasESaldo': 'Bets and balance',
  'taco.titulo': 'Cue',
  'taco.valeNaProxima': 'The cue applies from the next match on.',
  'taco.esperandoLogin': 'Sign in to see which cues this wallet holds.',
  'taco.somenteOsGratuitos': 'This wallet only holds the default cue.',
  'taco.raridade.gratuito': 'Free',
  'taco.raridade.common': 'Common',
  'taco.raridade.uncommon': 'Uncommon',
  'taco.raridade.rare': 'Rare',
  'taco.raridade.epic': 'Epic',
  'taco.raridade.legendary': 'Legendary',
  // The table's aim: how far the guide line reaches, chosen by whoever creates
  // the table and applied to both players.
  // The public vault page: where each match's money goes, and how the weekly
  // prize is doing.
  'nav.cofre': 'Vault',
  'nav.cofreTitulo': 'Where the money goes and how this week’s prize is doing',
  'cofre.tituloPagina': 'SOL Pool — Vault',
  'cofre.titulo': 'The vault',
  'cofre.subtitulo':
    'Where the money from each match goes, how much has piled up, and how far along this week’s prize is. All read from the blockchain, live.',
  'cofre.carregando': 'Reading…',
  'cofre.naoLido': 'could not be read',
  'cofre.poteTitulo': 'Where the pot goes',
  'cofre.poteExplica':
    'These percentages are read from the on-chain contract, not written into this page. If they change, this table changes by itself.',
  'cofre.poteNaoLido': 'Could not read the split rules from the blockchain. Try reloading.',
  'cofre.fatia.vencedor': 'Winner',
  'cofre.fatia.casa': 'House',
  'cofre.fatia.rank': 'Weekly prize',
  'cofre.fatia.tesouro': 'Treasury',
  'cofre.vaiDireto': 'goes straight to the wallet',
  'cofre.cofresTitulo': 'The vaults',
  'cofre.rank': 'Weekly prize',
  'cofre.casa': 'House',
  'cofre.tesouro': 'Treasury',
  'cofre.entrou': 'Taken in',
  'cofre.saiu': 'Paid out',
  'cofre.conta': 'On-chain account',
  'cofre.semanaTitulo': 'Week {id}',
  'cofre.acumulado': 'Accumulated',
  'cofre.pagoEm': 'Payment',
  'cofre.verTransacao': 'view the transaction',
  'cofre.semEntradas': 'Nobody has scored this week yet.',
  'cofre.fechaEm': 'Closes in {tempo}.',
  'cofre.atrasada': 'Due {tempo} ago and still not distributed.',
  'cofre.semanaNaoLida': 'Could not read the week. The vaults above are still valid.',
  'cofre.historicoTitulo': 'Previous weeks',
  'cofre.semanaBotao': 'Week {id}',
  'cofre.semHistorico': 'No week has been distributed yet.',
  'cofre.rodape':
    'This page only reads. Balances come from the program accounts on the blockchain and can be checked by anyone through the links above.',
  'cofre.duracao.diasHoras': '{dias}d {horas}h',
  'cofre.duracao.horas': '{horas}h',
  'cofre.duracao.minutos': '{minutos}min',
  'lobby.mira': 'Table aim',
  'mira.sem': 'No aim',
  'mira.sem.explica':
    'The screen shows nothing about the shot: no line, no contact point. Just the cue pointing.',
  'mira.padrao': 'Normal aim',
  'mira.padrao.explica':
    'The line to the ball, the contact point and a short stretch of what follows — the usual table.',
  'mira.estendida': 'Extended aim',
  'mira.estendida.explica':
    'The whole path, cushions included: you can see whether the ball drops before you shoot.',
  'mira.custom': 'Custom',
  'mira.custom.curto': '{m} m aim',
  'mira.custom.rotulo': 'Aim reach, in metres',
  'mira.custom.explica':
    'The screen shows {m} m of the path after contact. It applies to both players.',
  'taco.attr.peso': 'Weight',
  'taco.attr.efeito': 'Spin',
  'taco.attr.aderencia': 'Grip',
  'taco.attr.quebra': 'Break',
  'taco.visor.ver': 'View cue',
  'taco.gdm.ajuda': 'Gangue do Macaco: each NFT trait becomes a stat, and the rarer, the stronger.',
  'taco.visor.usar': 'Use this cue',
  'taco.visor.emUso': 'In use',
  'taco.visor.fechar': 'Close',
  'taco.padrao': 'Standard',
  'taco.padrao.ajuda': 'Balanced. The game was tuned with this one.',
  'taco.pesado': 'Heavy',
  'taco.pesado.ajuda': 'Breaks harder, less cue ball control.',
  'taco.preciso': 'Control',
  'taco.preciso.ajuda': 'Holds the cue ball where you left it, hits softer.',
  'taco.efeito': 'Spin',
  'taco.efeito.ajuda': 'More cue ball control, average power.',
  'taco.mint.botao': 'Mint my cue — {preco} SOL · {restantes} of {teto}',
  'taco.mint.botaoGratis': 'Mint my cue — free · {restantes} of {teto}',
  'taco.mint.retomar': 'Resume minting (already paid)',
  'taco.mint.esgotado': 'Test cues sold out',
  'taco.mint.indisponivel': 'Minting unavailable right now',
  'taco.mint.cunhando': 'Minting…',
  'taco.mint.veio': 'A {raridade} {arquetipo} arrived. It is already in the picker.',
  'taco.mint.naoPode': 'This wallet cannot mint right now.',

  // NFT cue levels: one "+" per bar, priced for the NEXT level of that
  // attribute. Greyed out, the tooltip says why — same sentence the server
  // would send as an event if the click did happen.
  'taco.upgrade.botao': '+ {preco} SOL',
  'taco.upgrade.botaoGratis': '+ free',
  'taco.upgrade.botaoIndisponivel': '+',
  'taco.upgrade.subirTitulo': 'Buy one level of this attribute for {preco} SOL.',
  'taco.upgrade.subirTituloGratis': 'Buy one level of this attribute, for free.',
  'taco.upgrade.niveis': 'levels {gastos}/{orcamento}',
  'taco.upgrade.niveisTitulo': 'Levels already bought on this cue, out of what its rarity allows.',
  'taco.upgrade.nivelDoAtributo': '{attr} {nivel}/{max}',
  'taco.upgrade.investido': 'invested {sol} SOL',
  'taco.upgrade.investidoTitulo': 'Total already paid in levels on this cue.',
  'taco.upgrade.subindo': 'Buying the level…',
  'taco.upgrade.subiu': 'Bought one level of {atributo}.',
  'taco.upgrade.naoPode': 'This attribute cannot level up right now.',
  'seletor.titulo': 'Your cues',
  'seletor.trocar': 'Change cue',
  'seletor.quantos': { one: '{count} cue', other: '{count} cues' },
  'seletor.filtros': 'Cue filters',
  'seletor.buscar': 'Search by name or number (#12)',
  'seletor.ordem': 'Sort',
  'seletor.ordem.raridade': 'Rarest first',
  'seletor.ordem.forca': 'Strongest first',
  'seletor.ordem.numero': 'By number',
  'seletor.ordem.niveis': 'Most upgraded first',
  'seletor.todas': 'All',
  'seletor.melhorados': 'Upgraded',
  'seletor.anterior': 'Previous',
  'seletor.proxima': 'Next',
  'seletor.pagina': 'Page {pagina} of {total}',
  'seletor.mostrando': '{inicio}–{fim} of {total}',
  'seletor.nenhum': 'No cues match these filters.',
  'seletor.limpar': 'Clear filters',
  'seletor.fechar': 'Close',
  'carteira.abrir': 'Wallet: balance, nickname and sign out',

  // --------------------------------------------------------- the mint tab
  'mint.titulo': 'Mint your test cue',
  'mint.sub1': 'The collection lives on Solana devnet — the test network.',
  'mint.sub2': 'Devnet SOL is not worth money: it is free and only good for testing.',
  'mint.preco': '{preco} SOL per cue',
  'mint.precoDesconhecido': '—',
  'mint.contador': '{n} of {teto} minted',
  'mint.conecte': 'Connect the wallet above to mint yours.',
  'mint.seuTaco': 'Your cue',
  'mint.usarNoJogo': 'Use it in game',
  'mint.faltaSol': 'This wallet does not have enough SOL for the cue plus the network fee.',
  'mint.indisponivel': 'Minting is turned off on this server.',
  'mint.esgotado': 'The test collection is sold out — no cues left to mint.',
  'controle.titulo': 'How to shoot',
  'controle.doisGestos': 'Aim and shoot separately',
  'controle.doisGestosAjuda': 'Drag the cloth to aim; pull the cue to shoot',
  'controle.arrastoUnico': 'Single drag',
  'controle.arrastoUnicoAjuda': 'Drag from the cue ball: aim and power together, release shoots',
  'chat.treino': 'Practice mode: chat only exists on a table with an opponent.',
  'nav.novidades': 'What’s new',
  'nav.novidadesTitulo': 'What changed in the game, version by version',
  'hud.girar': 'Rotate screen',
  'hud.menu': 'Menu',
  'hud.efeito': 'Spin',
  'hud.som': 'Sound',
  'hud.conversa': 'Chat',
  'hud.voz': 'Voice',
  'hud.fechar': 'Close',
  'menu.aba.partida': 'Game',
  'menu.aba.taco': 'Cue',
  'menu.aba.ajustes': 'Settings',
  'menu.tipo.livreSub': 'No rules: pick the balls and replay the shot.',
  'menu.tipo.miniSub': 'Practice the lag for the break against your best mark.',
  'mira.sem.sub': 'More challenging',
  'mira.padrao.sub': 'Line up to the ball',
  'mira.estendida.sub': 'The full line',
  'tema.rotulo': 'Button theme',
  'tema.classico': 'Classic',
  'tema.dourado': 'Gold',
  'tema.neon': 'Neon',
  'tema.oceano': 'Ocean',
  'mesaVisual.rotulo': 'Table color',
  'modeloMesa.rotulo': 'Table style',
  'modeloMesa.classica': 'Classic',
  'modeloMesa.moderna': 'Modern',
  'modeloMesa.luxo': 'Luxury',
  'modeloMesa.rustica': 'Rustic',
  'mesaVisual.classica': 'Classic',
  'mesaVisual.azul': 'Blue',
  'mesaVisual.vinho': 'Wine',
  'mesaVisual.grafite': 'Graphite',
  'hud.aviso.ajusteLigado': 'Fine aim on: the aim moves 5× slower',
  'hud.aviso.ajusteDesligado': 'Fine aim off',
  'hud.aviso.travaLigada': 'Aim locked: dragging only changes the power',
  'hud.aviso.travaDesligada': 'Aim unlocked',
  'hud.aviso.somLigado': 'Sound on',
  'hud.aviso.somDesligado': 'Sound off',
  'painelMesa.conversa': 'Chat',
  'painelMesa.voz': 'Voice',
  'voz.titulo': 'Voice',
  'voz.entrar': 'Join voice',
  'voz.sair': 'Leave voice',
  'voz.microfoneCalar': 'Mute my microphone',
  'voz.microfoneLigar': 'Unmute my microphone',
  'voz.sozinho': 'Nobody else on voice yet.',
  'voz.volumeDe': "{quem}'s volume",
  'voz.semMicrofone': 'No microphone: you can listen but not talk. Allow the microphone in the site permissions and join again.',
  'voz.semSuporte': 'This browser has WebRTC turned off, and voice needs it. It is usually a privacy extension or VPN disabling the feature. Turn it off for this page, or use another browser.',
  'voz.semConexao': 'Could not open the audio. Your network may be blocking the voice connection. Try again.',
  'voz.indisponivel': 'Voice is unavailable right now. The match carries on.',
  'evento.voz.desligada': 'This table has voice turned off.',
  'evento.voz.fora_da_mesa': 'Only people at the table — playing or watching — can join voice.',
  'evento.voz.indisponivel': 'Voice is unavailable right now. The match carries on.',

  // -------------------------------------------------- fim de partida (enum)
  'fim.regras': 'rules',
  'fim.desistência': 'forfeit',
  'fim.tempo': 'time',
  'fim.replay cheio': 'replay full',
  'fim.motivo.eight-ball-potted': 'The 8-ball was pocketed legally',
  'fim.motivo.eight-ball-early': 'The 8-ball dropped too early',
  'fim.motivo.eight-ball-wrong-pocket': 'The 8-ball dropped in the wrong pocket',
  'fim.motivo.eight-ball-with-foul': 'The 8-ball dropped on a foul shot',
  'fim.motivo.eight-ball-off-table': 'The 8-ball left the table',
  'fim.motivo.three-fouls': 'Three consecutive fouls',
  'fim.motivo.forfeit': 'Forfeit',
  'fim.motivo.all-balls-potted': 'All balls pocketed',
  'fim.motivo.score-unreachable': 'Score out of reach',

  // -------------------------------------- decisões (kind + índice canônico)
  'decisao.eight-on-break': '8-ball pocketed on the break',
  'decisao.illegal-break': 'Illegal break',
  'decisao.opcao.0': 'Accept the table',
  'decisao.opcao.1': 'Re-break',
  'decisao.opcao.2': 'Hand over the break',
  'decisao.opcao.3': 'Spot the 8-ball',

  // ------------------------------------------------------------------- rede
  'rede.mainnet': 'MAINNET',
  'rede.real': 'Real money. Every table spends real SOL.',
  'rede.local': 'Local network on your machine. Nothing here exists outside it.',
  'rede.teste': 'Test network. The SOL used here has no value.',
  'rede.conectado': 'connected',
  'rede.conectando': 'connecting…',
  'rede.offline': 'offline',
  'rede.escrow': 'on-chain escrow',
  'rede.contratoTitulo': 'Contract that holds the deposits',
  'rede.contrato': 'contract {endereco}',
  'rede.rpcPublico': 'public RPC',
  'rede.rpcPublicoTitulo':
    'Set VITE_RPC_URL to a dedicated RPC (Helius, QuickNode, Triton). The public Solana endpoint rejects calls with 403 once you exceed its limit.',

  // -------------------------------------------------------------- cabeçalho
  'cabecalho.subtitulo': '8-Ball 1v1 wagered in {simbolo}. Tables with on-chain escrow.',

  // ---------------------------------------------------------------- entrada
  'entrada.aguardandoCarteira': 'Waiting for the wallet…',
  'entrada.titulo': 'Sign in to play',
  'entrada.passos': 'Steps to sign in',
  'entrada.passo1': 'Get a Solana wallet',
  'entrada.passo1Sub': 'Phantom, Solflare or Backpack.',
  'entrada.passo2': 'Connect the wallet',
  'entrada.passo2Sub': 'Pick yours from the list.',
  'entrada.passo3': 'Sign to enter',
  'entrada.passo3Sub': 'It is free and moves no SOL.',
  'entrada.assinarExplica': 'Signing only proves the wallet is yours: it costs nothing and authorizes no transaction.',
  'entrada.celularExplica': 'On a phone, the game opens inside your wallet app. Tap yours:',
  'entrada.abrirNoApp': 'Open in {carteira}',
  'entrada.celularSemApp': 'Don’t have it yet? The button takes you to the wallet’s page, which sends you to the store.',
  'entrada.computadorExplica': 'No wallet found in this browser. Install an extension:',
  'entrada.instalar': 'Install {carteira}',
  'entrada.jaInstalei': 'Installed it, reload',
  'entrada.treinarSemCarteira': 'Just looking: practice without a wallet',
  'entrada.reconectando': 'Reconnecting your wallet…',
  'entrada.assinarLogin': 'Sign in',
  'entrada.desconectar': 'Disconnect wallet',
  'entrada.conectadoCom':
    'Connected with {carteira}. The login signature is free and moves no funds. Deposits are separate transactions, which you approve one by one.',
  'entrada.jogaDeVerdade': 'You will play with real {simbolo} on {rede}.',
  'entrada.semCarteira':
    'No Solana wallet found in this browser. Install one — {phantom}, {solflare}, {backpack} and others work — and it will show up here on its own.',
  'entrada.recusadas':
    'Not usable here: {lista}. This table requires a wallet that signs the transaction without broadcasting it, so the match never lands on the wrong network.',

  // ------------------------------------------------------- dinheiro em jogo
  'emJogo.titulo': 'Money at stake',
  'emJogo.esperando': 'waiting for an opponent',
  'emJogo.prazoEm': 'in play · deadline in {min} min',
  'emJogo.vencidoHa': 'deadline passed {min} min ago',
  'emJogo.destravar': 'Unlock and get it back',
  'emJogo.nota':
    "Read from the contract, not from our server — so it shows up even if the server is down. Once the deadline passes, anyone can unlock it, and the contract only returns funds to the two players.",
  'emJogo.devolvido': 'Deposit returned to both players.',

  // ------------------------------------------------------------- minha mesa
  'minhaMesa.aberta':
    'Your table is open and the deposit is locked in the contract. It leaves the list in {contador} if nobody joins — the money is still yours, just cancel.',
  'minhaMesa.fechada': 'Table closed with {adversario}. Both deposits are in the contract.',
  'minhaMesa.mesa': 'Table',
  'minhaMesa.modalidade': 'Game',
  'minhaMesa.entrada': 'Stake',
  'minhaMesa.pote': 'Pot',
  'minhaMesa.vencedorLeva': 'Winner takes',
  'minhaMesa.adversario': 'Opponent',
  'minhaMesa.cancelando': 'Cancelling…',
  'minhaMesa.espectadores': 'Spectators',
  'minhaMesa.espectadoresSim': 'Allowed',
  'minhaMesa.espectadoresNao': 'Just the two of you',
  'minhaMesa.senha': 'Password',
  'minhaMesa.sim': 'Yes',
  'minhaMesa.nao': 'No',
  'minhaMesa.proximoPasso': 'What happens next',
  'minhaMesa.proximoPasso.aguardando':
    'When someone joins and deposits, the match opens on its own and you are taken to the table. Meanwhile, send the invite.',
  'minhaMesa.proximoPasso.fechada':
    'Both deposits are in the contract. Enter the table: the break is agreed once both of you are there.',
  'minhaMesa.cancelar': 'Cancel and get it back',
  'minhaMesa.reembolso':
    'If the match does not start, both deposits come back on their own in {contador}.',
  'minhaMesa.reembolsoVencido':
    'The table deadline has passed: the server is refunding the deposits. It may take a few minutes.',
  'minhaMesa.entrar': 'Go to the table',
  'minhaMesa.notaCancelar':
    'Cancelling is a transaction: the wallet will ask for approval and the deposit comes back right away.',
  'minhaMesa.notaFechada':
    'Both deposits are in the contract. The match starts as soon as both players are at the table.',

  // ------------------------------------------------------------------ lobby
  'lobby.entrar': 'Join',
  'lobby.sair': 'Sign out',
  'lobby.criarTitulo': 'Create or join a table',
  'lobby.criarMesa': 'Create table',
  'lobby.modalidade': 'Game',
  'lobby.notaCriar':
    'Creating or joining a table is a real transaction on {rede}. The deposit stays in the contract until the match ends.',
  'lobby.mesasAbertas': 'Open tables',
  'lobby.treinar': 'Practice',
  'lobby.treinarEnquantoEspera': 'Or practice while you wait',
  'lobby.nenhumaMesa': 'No open tables. Create the first one.',
  'lobby.nenhumTorneio': 'No tournaments open. Create the first one and call your crew.',
  'lobby.nenhumaSerie': 'No best-of-3 open. Create the first one.',
  'lobby.mesas': 'Tables',
  'lobby.assistir': 'Watch',
  'lobby.permitirEspectadores': 'Allow spectators',
  'lobby.emDisputa': 'in play',
  'lobby.plateiaAberta': 'spectators welcome',
  'lobby.suasPartidas': 'Your matches',
  'lobby.notaHistorico':
    'Read straight from the blockchain. Every match is replayed here in your browser: the badge confirms that the recorded bytes match the settlement hash and lead to a winner.',
  'lobby.solDeTesteRecebido': 'Test SOL received.',

  // ------------------------------------------------------ create table (modal)
  'criar.titulo': 'Create table',
  'criar.etapa': 'Step {n} of 2',
  'criar.fechar': 'Close',
  'criar.entrada': 'Stake',
  'criar.outroValor': 'Other amount',
  'criar.faixa': 'Between {min} and {max} {simbolo}',
  'criar.faixaDesconhecida': 'Reading the contract limits…',
  'criar.saldo': 'Balance: {saldo} {simbolo}',
  'criar.erro.invalida': 'Invalid amount. Use digits, with a dot or comma.',
  'criar.erro.abaixoDoMinimo': 'Below the contract minimum: {min} {simbolo}.',
  'criar.erro.acimaDoMaximo': 'Above the contract maximum: {max} {simbolo}.',
  'criar.erro.excedeSaldo':
    'Exceeds your balance: stake plus fees need {necessario} {simbolo}, and the wallet has {tem}.',
  'criar.semSaldoUnica':
    'Your wallet has no {simbolo}. A single match needs a deposit; best of 3 and tournament are friendly and can be created with no balance.',
  'criar.verRegras': 'see rules ↗',
  'criar.nome': 'Table name',
  'criar.nomeOpcional': 'optional · up to {max} characters',
  'criar.placeholderNome': "E.g. Joe's table",
  'criar.espectadoresExplica': 'Anyone can watch the match.',
  'criar.previa': 'How it plays out',
  'criar.pote': 'Total pot (2 × stake)',
  'criar.vencedorLeva': 'Winner takes ({pct})',
  'criar.corteCasa': 'House ({pct})',
  'criar.corteRank': 'Rank vault ({pct})',
  'criar.corteTesouraria': 'Treasury ({pct})',
  'criar.percentuaisLendo': 'Reading the contract percentages…',
  'criar.percentuaisDaChain': 'Percentages read from the contract, not typed here.',
  'criar.prazos': 'Deadlines',
  'criar.prazoTacada': 'Per shot',
  'criar.prazoRevelacao': 'Reveal the break',
  'criar.prazoDuracao': 'Maximum length',
  'criar.continuar': 'Continue',
  'criar.voltar': 'Back',
  'criar.cancelar': 'Cancel',
  'criar.confirmar': 'Confirm the table',
  'criar.resumo.entrada': 'Stake',
  'criar.resumo.pote': 'Pot',
  'criar.resumo.seVencer': 'If you win, you get',
  'criar.resumo.modalidade': 'Game',
  'criar.resumo.nome': 'Name',
  'criar.resumo.semNome': 'unnamed',
  'criar.resumo.espectadores': 'Spectators',
  'criar.resumo.espectadoresSim': 'yes, open to the crowd',
  'criar.resumo.espectadoresNao': 'no, just the two players',
  'criar.resumo.prazoTacada': 'Shot clock',
  'criar.notaConfirmar':
    'Confirming asks your wallet to sign a real transaction on {rede}. The deposit stays in the contract until the match ends, and comes back if nobody joins.',
  'criar.depositarECriar': 'Deposit and create',
  'criar.senha': 'Table password',
  'criar.senhaOpcional': 'optional · {min} to {max} characters',
  'criar.senhaExplica': 'Only those who know the password can join or watch. It is not stored anywhere.',
  'criar.placeholderSenha': 'No password',
  'criar.mostrarSenha': 'Show password',
  'criar.ocultarSenha': 'Hide password',
  'criar.erro.senhaCurta': 'Password too short: at least {min} characters.',
  'criar.erro.senhaLonga': 'Password too long: at most {max} characters.',
  'criar.resumo.senha': 'Password',
  'criar.resumo.senhaSim': 'yes, password only',
  'criar.resumo.senhaNao': 'no, open table',

  // ------------------------------------------------------------ lobby tabs
  'abas.rotulo': 'Lobby sections',
  'abas.mesas': 'Tables',
  'abas.emjogo': 'In play',
  'abas.partidas': 'Your matches',
  'abas.ranking': 'Ranking',
  'abas.cunhar': 'Mint',
  'emJogo.vazio': 'None of your deposits is held in the contract right now.',

  // ---------------------------------------------------------- table filters
  'filtro.rotulo': 'Filter tables',
  'filtro.abertas': 'Open',
  'filtro.disputa': 'In play (spectators welcome)',
  'filtro.todas': 'All',
  'filtro.modoTodas': 'All games',
  'filtro.nenhuma': 'No table matches this filter.',

  // ---------------------------------------------------- table detail (modal)
  'sala.detalhes': 'Details of table {nome}',
  'sala.subtitulo': 'Table',
  'sala.estado': 'Status',
  'sala.estado.waiting': 'Open, waiting for an opponent',
  'sala.estado.committed': 'Closed, both deposited',
  'sala.estado.playing': 'In play',
  'sala.estado.settled': 'Finished, awaiting settlement',
  'sala.criador': 'Creator',
  'sala.oponente': 'Opponent',
  'sala.voce': 'you',
  'sala.saiDaListaEm': 'Leaves the list in',
  'sala.prazoVencido': 'expired',
  'sala.prazoContrato': 'Contract deadline',
  'sala.prazoContratoValor': '{min} min after it closes',
  'sala.entrarEDepositar': 'Join and deposit',
  'sala.notaEntrar':
    'Joining deposits {valor} {simbolo} into the contract, signed by your wallet. Both deposits stay there until the match ends.',
  'sala.semAcao': 'This table is closed to outsiders.',
  'sala.sumiu': 'This table is no longer listed.',

  // -------------------------------------------------------------- invite
  'convite.entrarComCodigo': 'Join with a code',
  'convite.explicaCampo': 'Got a 6-character code? Type it here to open the table.',
  'convite.placeholder': 'K7M-2PX',
  'convite.abrir': 'Open table',
  'convite.procurando': 'Looking…',
  'convite.titulo': 'Invite someone',
  'convite.codigo': 'Table code',
  'convite.explica': 'Anyone with the code, or the link, finds this table in the lobby.',
  'convite.copiarLink': 'Copy invite',
  'convite.copiarCodigo': 'Copy code',
  'convite.copiado': 'Copied!',
  'convite.falhouCopiar': 'Could not copy. Select it and copy by hand.',
  'convite.linkComSenha': 'The copied link carries the password: send it only to who should join.',
  'convite.linkSemSenha':
    "This table's password is not in the link (it is not stored): share it separately.",
  'sala.protegida': 'Password-protected table',
  'sala.senha': 'Table password',
  'sala.senhaExplica': 'This table has a password. Ask whoever created it and type it below.',
  'sala.senhaVazia': 'Type the password to continue.',

  // ----------------------------------------------------------------- faucet
  'faucet.semSol': 'This wallet has no SOL on {rede}.',
  'faucet.semSolDevnet':
    'No SOL on {rede}. Mainnet SOL does not show up here — they are separate networks.',
  'faucet.precisaMais': 'Need more test SOL?',
  'faucet.pedindo': 'Requesting…',
  'faucet.pedir': 'Request test SOL',

  // ------------------------------------------------------------------ motor
  'motor.indisponivel': 'Wagering unavailable in this browser',
  'motor.explicacao':
    "The physics must produce exactly the same result everywhere — that is what lets a match's replay prove who won. Where we have not verified it yet, we would rather not let money in.",
  'motor.verificar': 'Run the check here',

  // ------------------------------------------------------------ retrospecto
  'retrospecto.titulo': 'Your record',
  'retrospecto.vitorias': { one: 'win', other: 'wins' },
  'retrospecto.derrotas': { one: 'loss', other: 'losses' },
  'retrospecto.aproveitamento': 'win rate',
  'retrospecto.saldo': 'Net from matches',

  // ------------------------------------------------------------ vault ranking
  'ranking.titulo': 'Vault ranking',
  'ranking.cofre': 'This week’s vault',
  'ranking.saldoAtrasado': 'updates with the blockchain every 5 min',
  'ranking.semana': 'Week {id}',
  'ranking.emDisputa': 'in progress',
  'ranking.fechaEm': 'closes in {tempo}',
  'ranking.distribuidoEm': 'distributed on {data}',
  'ranking.total': 'Total distributed',
  'ranking.colPosicao': '#',
  'ranking.colJogador': 'Player',
  'ranking.colPontos': 'Points',
  'ranking.colPremio': 'Prize',
  'ranking.voce': 'you',
  'ranking.suaPosicao': 'Your position this week: {posicao}',
  'ranking.semPontuar': 'You haven’t scored this week yet.',
  'ranking.vazio': 'No one has scored this week yet.',
  'ranking.carregando': 'Loading the ranking…',
  'ranking.indisponivel': 'Ranking unavailable on this server.',
  'ranking.erroCarregar': 'Could not load the ranking.',
  'ranking.semanaDesconhecida': 'That week does not exist.',
  'ranking.mesSemSemana': 'No week registered in this month.',
  'ranking.mesAnterior': 'Previous month',
  'ranking.mesSeguinte': 'Next month',
  'ranking.verNaChain': 'view distribution on chain',
  'ranking.voltar': 'Back',
  'ranking.perfilGraficoAlt': '{jogador} points per week chart',
  'ranking.perfilGraficoTitulo': 'Points per day · last {dias} days',
  'ranking.perfilMelhorDia': 'best day: {pontos} pts',
  'ranking.perfilBarraDica': '{data} · {partidas} match(es) · {pontos} pts',
  'ranking.perfilResumoLinha': '{partidas} match(es) · {adversarios} opponent(s) · {pontos} pts{posicao}',
  'ranking.perfilPartidas': 'Matches',
  'ranking.perfilSemPartidas': 'This player has no settled matches yet.',
  'ranking.perfilPartidasErro': 'Could not load the matches.',
  'ranking.perfilVitoria': 'win',
  'ranking.perfilDerrota': 'loss',
  'ranking.perfilPartidaLinha': '{premio} · {pontos} pts',
  'ranking.perfilPagina': 'Page {pagina} of {total}',

  // -------------------------------------------------------------- histórico
  'historico.vazio': 'No settled matches yet.',
  'historico.vitoria': 'Win',
  'historico.derrota': 'Loss',
  'historico.poteTitulo': 'Match pot: {valor}',
  'historico.pote': '{valor} (pot)',
  'historico.noPote': '{valor} in the pot',
  'historico.reproduz': 'The recorded replay reproduces this result.',
  'historico.selo.confere': '✓ replay verified',
  'historico.selo.divergiu': '⚠ replay diverges',
  'historico.selo.erro': '· not verifiable',
  'historico.selo.indisponivel': '· replay not archived',
  'historico.versaoTitulo':
    'Replay format and physics version the match was played with. A match only replays identically on the physics that produced it.',
  'historico.versao': 'fmt v{formato} · phys v{fisica}',
  'historico.assistir': '▶ watch the match',
  'historico.motivo.naoArquivado':
    "this replay's bytes are not archived. The on-chain commitment is still valid: any copy that turns up can be checked against it.",
  'historico.motivo.hashDiverge': 'the served bytes do not match the hash recorded at settlement',
  'historico.motivo.semVencedor': 'the replay does not reach a winner',
  'historico.motivo.seed': 'the break seed did not come from the recorded nonces',
  'historico.motivo.vencedorDiverge': 'whoever received the pot is not who the replay says won',
  'historico.motivo.formatoAntigo':
    'Recorded with an earlier format version. Still auditable with the code from that time.',
  'historico.motivo.fisicaAntiga':
    'Recorded with an earlier physics version. Still auditable with the engine from that time.',

  // -------------------------------------------------------------- navegação
  'nav.lobby': 'Lobby',
  'nav.lobbyTitulo': 'Open tables and your history',
  'nav.treino': 'Practice',
  'nav.treinoTitulo': 'Play alone, no stake and no wallet',
  'nav.determinismo': 'Determinism',
  'nav.determinismoTitulo': 'Proof that the physics runs identically in any browser',
  'nav.regras': 'Rules',
  'nav.regrasTitulo': 'What you are playing for: rules, fouls, deadlines and limits',
  'nav.discord': 'Discord',
  'nav.conectarCarteira': 'Connect wallet',
  'nav.escolhaCarteira': 'Choose a wallet',
  'nav.discordTitulo': 'Join the server: ideas, problems and who is playing',

  // ----------------------------------------------------------------- replay
  'replay.tituloPagina': 'SOL Pool — Watch replay',
  'replay.cabecalho': 'Verified replay',
  'replay.intro':
    'The bytes are checked against the hash recorded on the blockchain before anything is drawn. A match that does not verify is not replayed.',
  'replay.tocar': 'Play',
  'replay.pausar': 'Pause',
  'replay.fim': 'End',
  'replay.proximaTacada': 'Next shot',
  'replay.simulacaoTitulo': 'Simulation, shot by shot',
  'replay.col.jogador': 'plr',
  'replay.col.angulo': 'angle',
  'replay.col.forca': 'power',
  'replay.col.efeito': 'spin',
  'replay.col.hash': 'state hash',
  'replay.col.naMesa': 'on table',
  'replay.col.julgamento': 'ruling',
  'replay.toqueParaComecar': 'Press play to start.',
  'replay.notaHash':
    'The <span class="font-mono">state hash</span> is the same one client and server exchange during the match to detect divergence. Recompute it on your machine from these bytes: if it matches, the physics ran identically there.',
  'replay.registroOnChain': 'On-chain record',
  'replay.status.lendoRegistro': 'Reading the on-chain record…',
  'replay.status.buscandoBytes': 'Fetching the replay bytes…',
  'replay.status.verificando': 'Checking compatibility and replaying the match…',
  'replay.erro.partidaInvalida': 'Invalid match',
  'replay.erro.partidaInvalidaDetalhe': 'Use ?match=<32-character hex id>.',
  'replay.erro.naoEncontrada': 'Match not found',
  'replay.erro.naoEncontradaDetalhe': 'There is no settled record with this id.',
  'replay.erro.naoArquivado': 'Replay not archived',
  'replay.erro.naoArquivadoDetalhe':
    'The on-chain commitment is still valid — we just do not have the bytes. Anyone with a copy can publish it on-chain, and it becomes valid for everyone.',
  'replay.erro.bytesNaoConferem': 'The bytes do not match',
  'replay.erro.bytesNaoConferemDetalhe':
    'The SHA-256 of the received replay does not match the one recorded at settlement. This is not an old match: it is a wrong or tampered file.',
  'replay.erro.incompleto': 'Incomplete replay',
  'replay.erro.incompletoDetalhe': 'The re-execution does not reach a winner.',
  'replay.erro.vencedorDivergente': 'Winner mismatch',
  'replay.erro.vencedorDivergenteDetalhe':
    'Whoever received the pot is not who the replay says won. We will not replay this as if it were the match.',
  'replay.verificado': 'Verified.',
  'replay.verificadoDetalhe':
    'The bytes match the hash recorded on-chain, and the re-execution confirms the winner that was paid.',
  'replay.semVideo': 'No video.',
  'replay.ficha.partida': 'match',
  'replay.ficha.vencedor': 'winner',
  'replay.ficha.perdedor': 'loser',
  'replay.ficha.pote': 'pot',
  'replay.ficha.liquidadaEm': 'settled at',
  'replay.ficha.replay': 'replay',
  'replay.ficha.nonceCriador': 'creator nonce',
  'replay.ficha.nonceOponente': 'opponent nonce',
  'replay.ficha.seed': 'break seed',
  'replay.ficha.formato': 'format',
  'replay.ficha.fisica': 'physics',
  'replay.ficha.modalidade': 'game',
  'replay.bytes': '{n} bytes',
  'replay.info.tacada': 'shot {atual} of {total}',
  'replay.info.vez': "player {n}'s turn",
  'replay.info.vencedorGravado': 'recorded winner: {quem}',
  'replay.criador': 'creator',
  'replay.oponente': 'opponent',
  'replay.vezPassa': 'turn passes',
  'replay.cacapaDeclarada': '(pocket {n})',
  'replay.partidaEncerrada': '· match over',
  'replay.engineIncompativel':
    'This match was played on physics v{doReplay} and this code runs v{daEngine}. The replay is still valid and verifiable — what is missing here is the physics that produced it.',

  // ----------------------------------------------------------- determinismo
  'determinismo.tituloPagina': 'Engine determinism',
  'determinismo.voltar': '← Back to the site',
  'determinismo.sobrancelha': 'determinism check · physics v{versao}',
  'determinismo.legendaCima': 'top — what THIS browser computed',
  'determinismo.legendaBaixo': 'bottom — what is recorded as reference',
  'determinismo.concorda': 'This browser agrees with the server.',
  'determinismo.divergiu': 'Diverged in {falhas} of {total} matches.',
  'determinismo.explicaOk':
    "The {total} reference matches reproduced the recorded hashes exactly. Shot prediction on the client and public replay audits both depend on this: a single divergent hash would leave neither able to prove anything.",
  'determinismo.explicaFalha':
    'While this lasts, shot prediction and replay audits are not valid in this browser — the table here evolves differently from the server. The marked columns above show where the digests split; the list below, which matches caused it.',
  'determinismo.partidas': 'matches',
  'determinismo.conferem': 'match',
  'determinismo.tempo': 'time',
  'determinismo.digest': 'digest',
  'determinismo.conferidas': 'checked',
  'determinismo.andamento': 'progress',
  'determinismo.trilhaNota': 'one segment per match — hover to see the name',
  'determinismo.trilhaAria': '{ok} of {total} matches verified',
  'determinismo.divergiuSufixo': ' — diverged',
  'determinismo.tabelaTitulo': 'matches that diverged',
  'determinismo.colPartida': 'match',
  'determinismo.colGravado': 'recorded',
  'determinismo.colObtido': 'computed here',
  'determinismo.rodape':
    'To check for yourself: <code>bun run determinism</code> opens this page in every installed browser, in a clean profile, and compares the verdicts. The result is also available at <code>window.__determinism</code>.',
  'determinismo.conferindo': 'Checking…',
  'determinismo.esperaExplica':
    'This browser is replaying the reference matches and comparing each hash with what is recorded. It takes over thirty seconds of computation — the page stays responsive meanwhile, because the work runs on a separate thread.',
  'determinismo.semWorker': 'This browser has no Web Worker.',
  'determinismo.semWorkerExplica':
    'The battery can only run by freezing the page, for over thirty seconds. Open it in a browser with worker support, or run <code>bun test</code> on the server.',
  'determinismo.naoRodou': 'The battery did not run.',
  'determinismo.naoRodouExplica':
    'The worker failed to load. This is NOT a determinism divergence — the tool broke, and the verdict remains unknown.',
  'determinismo.semReferencia': '(no reference)',

  // ------------------------------------------------------------------ admin
  'admin.tituloPagina': 'SOL Pool — Admin',
  'admin.titulo': 'Administration',
  'admin.faixa': 'Administration panel — read directly from the blockchain.',
  'admin.atualizadoAs': 'updated {hora}',
  'admin.carregando': 'loading…',
  'admin.atualizar': 'Refresh',
  'admin.lendo': 'Reading the blockchain…',
  'admin.configuracao': 'Configuration',
  'admin.configNaoEncontrado': 'Config not found on-chain.',
  'admin.programa': 'Program',
  'admin.autoridade': 'Authority',
  'admin.referee': 'Referee',
  'admin.entrada': 'Stake',
  'admin.divisao': 'Split',
  'admin.divisaoValor': 'winner {vencedor} · house {casa} · protocol {protocolo}',
  'admin.estado': 'State',
  'admin.pausado': 'PAUSED',
  'admin.operando': 'operating',
  'admin.cofres': 'Vaults',
  'admin.casa': 'House',
  'admin.protocolo': 'Protocol',
  'admin.sacado': 'withdrawn',
  'admin.queimado': 'burned',
  'admin.naoCriado': 'not created',
  'admin.entrou': 'received',
  'admin.partidas': { one: 'match', other: 'matches' },
  'admin.mesas': 'Tables',
  'admin.travados': '{valor} SOL locked',
  'admin.nenhumaMesa': 'No open tables.',
  'admin.col.conta': 'account',
  'admin.col.estado': 'state',
  'admin.col.entrada': 'stake',
  'admin.col.pote': 'pot',
  'admin.col.noCofre': 'in escrow',
  'admin.col.criador': 'creator',
  'admin.col.oponente': 'opponent',
  'admin.col.prazo': 'deadline',
  'admin.aguardando': 'waiting',
  'admin.comprometida': 'committed',
  'admin.destravar': 'Unlock',
  'admin.vencidaHa': 'expired {tempo} ago',
  'admin.semOponente': 'Table without an opponent: the creator cancels it, from the game itself.',
  'admin.destravada': 'Unlocked. Deposits returned to both players. ({assinatura})',
  'admin.recusou': 'You declined in your wallet.',
  'admin.conecteNaBarra': 'Connect the admin wallet in the top bar.',
  'admin.falhaLeitura': 'Failed to read the blockchain: {erro}',
  'admin.rodape':
    'Pausing, changing the referee and withdrawing from the vault require the authority key, which should not live in a browser. Use <code>bun scripts/admin.ts</code>.',

  // ------------------------------------------------------ admin: coleções
  'admin.colecoes.abaMesas': 'Tables and vaults',
  'admin.colecoes.aba': 'Collections',
  'admin.colecoes.titulo': 'Partner collections',
  'admin.colecoes.entrar': 'Sign in as admin',
  'admin.colecoes.entrarExplica':
    'The collections tab asks for a signature from the admin wallet. The session lasts 15 minutes, only reads and builds drafts, and is gone when the page reloads.',
  'admin.colecoes.voceVaiAssinar': 'You are about to sign:',
  'admin.colecoes.nenhuma': 'No collection registered.',
  'admin.colecoes.abrir': 'Open',
  'admin.colecoes.voltar': '← Collections',
  'admin.colecoes.col.nome': 'name',
  'admin.colecoes.col.rede': 'network',
  'admin.colecoes.col.estado': 'status',
  'admin.colecoes.col.cadastro': 'registered by',
  'admin.colecoes.col.distribuicao': 'distribution',
  'admin.colecoes.estado.rascunho': 'draft',
  'admin.colecoes.estado.em_analise': 'under review',
  'admin.colecoes.estado.aprovada': 'approved',
  'admin.colecoes.estado.recusada': 'refused',
  'admin.colecoes.estado.suspensa': 'suspended',
  'admin.colecoes.emAnaliseSelo': 'under review: v{versao}',
  'admin.colecoes.cadastradaAdmin': 'registered by the admin',
  'admin.colecoes.cadastradaAutoridade': 'registered by the owner',
  'admin.colecoes.alfabetos': '⚠ mixed alphabets',
  'admin.colecoes.alfabetosDica': 'Latin together with Cyrillic or Greek in the same word: that is how a fake name imitates a real one.',
  'admin.colecoes.cadastrar.titulo': 'Register on behalf of the owner',
  'admin.colecoes.cadastrar.explica':
    'The collection address (the verified collection NFT), the network and the owner wallet. The collection starts as a draft; nothing reaches the game without approval.',
  'admin.colecoes.cadastrar.endereco': 'Collection address',
  'admin.colecoes.cadastrar.rede': 'Network',
  'admin.colecoes.cadastrar.dono': 'Owner wallet',
  'admin.colecoes.cadastrar.botao': 'Sign and register',
  'admin.colecoes.holders': 'holders: {holders}',
  'admin.colecoes.modeloBadge': 'model: {modelo}',
  'admin.colecoes.dono': 'owner {dono}',
  'admin.colecoes.motivo': 'Recorded reason: {motivo}',
  'admin.colecoes.avisoPiso': 'Every cue in this collection is better than the free cue in everything. This affects the rank and the vault.',
  'admin.colecoes.avisoOrdem': 'Numbers came from the order of the mints (some NFT has no "#number" in its name); check before approving.',
  'admin.colecoes.versao.aprovada': 'approved',
  'admin.colecoes.versao.emAnalise': 'under review',
  'admin.colecoes.versao.rascunho': 'draft',
  'admin.colecoes.versao.tacos': '{total} cues, {comArte} with art',
  'admin.colecoes.versao.ver': 'View this one',
  'admin.colecoes.versao.naTela': 'on screen',
  'admin.colecoes.acao.enviar': 'Send v{versao} for review',
  'admin.colecoes.acao.aprovar': 'Sign and approve v{versao}',
  'admin.colecoes.acao.recusar': 'Sign and refuse',
  'admin.colecoes.acao.suspender': 'Sign and suspend',
  'admin.colecoes.acao.reativar': 'Sign and reactivate',
  'admin.colecoes.acao.motivo': 'Reason (refuse, suspend, reactivate)',
  'admin.colecoes.motivoRuim': 'Write a one-line reason, up to 200 characters, without double spaces.',
  'admin.colecoes.soLeitura': 'Collection registered by its owner: the table is theirs. Here you only review.',
  'admin.colecoes.tabela': 'NFT → cue table',
  'admin.colecoes.modelo': 'Points model',
  'admin.colecoes.salvarEdicoes': { one: 'Save draft ({count} change)', other: 'Save draft ({count} changes)' },
  'admin.colecoes.edicoes.descartar': 'Discard',
  'admin.colecoes.edicoes.explica': 'Saving creates a new draft version.',
  'admin.colecoes.selecao.quantas': { one: '{count} selected', other: '{count} selected' },
  'admin.colecoes.selecao.raridade': 'Rarity',
  'admin.colecoes.selecao.arquetipo': 'Archetype',
  'admin.colecoes.selecao.limpar': 'Clear selection',
  'admin.colecoes.selecao.visiveis': { one: 'Select the visible one', other: 'Select the {count} visible' },
  'admin.colecoes.selecao.marcar': 'Select #{numero}',
  'admin.colecoes.raridadeCurta.common': 'Com',
  'admin.colecoes.raridadeCurta.uncommon': 'Unc',
  'admin.colecoes.raridadeCurta.rare': 'Rare',
  'admin.colecoes.raridadeCurta.epic': 'Epic',
  'admin.colecoes.raridadeCurta.legendary': 'Leg',
  'admin.colecoes.vista.rotulo': 'View as',
  'admin.colecoes.vista.cards': 'Cards',
  'admin.colecoes.vista.tabela': 'Table',
  'admin.colecoes.modeloOpcao.ruleset-v3': 'ruleset-v3 — the budget of our own cues',
  'admin.colecoes.modeloOpcao.piso-do-gratuito': 'piso-do-gratuito — the free cue + rarity',
  'admin.colecoes.modeloOpcao.por-traits': 'por-traits (default) — points come from the NFT traits',
  'admin.colecoes.modeloSobre.ruleset-v3': 'Rarity and archetype spend the same budget as our own cues: nobody dominates anybody.',
  'admin.colecoes.modeloSobre.piso-do-gratuito': 'The free cue is the floor and rarity adds an extra; legendary is 100 in everything.',
  'admin.colecoes.modeloSobre.por-traits': 'The default: points come from the NFT’s own TRAITS — the free cue is the floor and each category feeds one attribute, the rarer the trait the more points. The archetype changes no number here: it only picks the family art for NFTs with no uploaded art. The one-of-one is born 100 in everything. Bunker method (PR #27).',
  'admin.colecoes.montador.categoriasPorTraits': 'Trait category → attribute fed',
  'admin.colecoes.montador.pecaUnica': 'one-of-one (100 in everything)',
  'admin.colecoes.montador.atinge': 'hits {quantas} NFTs',
  'admin.colecoes.montador.noPiso': '{quantos} of the {total} NFTs stay at the free cue floor: none of them has a fed trait. Check before sending.',
  'admin.colecoes.montador.alimenta': 'Attribute this category feeds',
  'admin.colecoes.montador.alvo.nenhum': '— feeds nothing —',
  'admin.colecoes.montador.alvo.peso': 'weight',
  'admin.colecoes.montador.alvo.efeito': 'spin',
  'admin.colecoes.montador.alvo.aderencia': 'grip',
  'admin.colecoes.montador.alvo.quebra': 'break',
  'admin.colecoes.montador.alvo.todos': 'all (base)',
  'admin.colecoes.montador.erro.sem_alvo': 'Pick the attribute that at least one category feeds: with por-traits that is the only source of points. With none, every NFT would stay at the free cue floor.',
  'admin.colecoes.montador.categorias': 'Trait category → archetype',
  'admin.colecoes.montador.ignorar': '— ignored —',
  'admin.colecoes.montador.semTraits': 'Reading the collection traits…',
  'admin.colecoes.montador.cortado': 'The collection has too many traits: the list shows the first 32 categories and up to 500 values of each.',
  'admin.colecoes.montador.vazios': 'Empty values (do not count for the archetype), comma separated',
  'admin.colecoes.montador.vaziosPorTraits': 'Empty values (worth no points), comma separated',
  'admin.colecoes.montador.faixas': 'Rarity bands (% of the collection, from the top of the ranking)',
  'admin.colecoes.montador.faixaAte': '{raridade} up to',
  'admin.colecoes.montador.excecoes': 'Exceptions (trait = value → fixed rarity and archetype)',
  'admin.colecoes.montador.excecoesPorTraits': 'Exceptions (trait = value → fixed rarity, and the one-of-one)',
  'admin.colecoes.montador.trait': 'trait',
  'admin.colecoes.montador.valor': 'value',
  'admin.colecoes.montador.tirar': 'Remove',
  'admin.colecoes.montador.maisExcecao': 'Add an exception',
  'admin.colecoes.montador.distribuicao': 'Distribution with these rules:',
  'admin.colecoes.montador.gerar': 'Generate from the rules',
  'admin.colecoes.montador.erro.sem_categoria': 'Pick the archetype of at least one category.',
  'admin.colecoes.montador.erro.faixas': 'Bands go from 0 to 100 and grow: epic ≤ rare ≤ uncommon.',
  'admin.colecoes.montador.erro.excecao': 'Every exception needs a trait and a value.',
  'admin.colecoes.csv.subir': 'Upload CSV',
  'admin.colecoes.csv.formato': 'Columns: mint,raridade,arquetipo. Nothing is saved if any line has an error.',
  'admin.colecoes.csv.recusado': 'CSV refused:',
  'admin.colecoes.csv.linha': 'Line {linha}: {motivo}',
  'admin.colecoes.csv.mint_fora_da_colecao': 'mint outside the collection',
  'admin.colecoes.csv.raridade_invalida': 'invalid rarity',
  'admin.colecoes.csv.arquetipo_invalido': 'invalid archetype',
  'admin.colecoes.csv.mint_repetido': 'repeated mint',
  'admin.colecoes.csv.formato_invalido': 'invalid format',
  'admin.colecoes.arte.arraste': 'Drop several PNGs named <number>.png here (or pick them):',
  'admin.colecoes.arte.ok': 'art for #{numero} saved (v{versao})',
  'admin.colecoes.arte.nomeRuim': 'the name must be <number>.png',
  'admin.colecoes.arte.naoTentado': 'not sent: the server asked to slow down; try again in 1 minute',
  'admin.colecoes.lista.titulo': 'Collection NFTs',
  'admin.colecoes.lista.carregando': 'Loading…',
  'admin.colecoes.lista.semVersao': 'No table yet. Generate the first one from the rules or upload a CSV.',
  'admin.colecoes.lista.soLeituraVersao': 'This is not the latest version: the list is read-only.',
  'admin.colecoes.lista.nada': 'No NFT matches these filters.',
  'admin.colecoes.lista.contagem': '{visiveis} of {total} NFTs',
  'admin.colecoes.lista.nft': 'NFT',
  'admin.colecoes.lista.mint': 'mint',
  'admin.colecoes.lista.raridade': 'rarity',
  'admin.colecoes.lista.arquetipo': 'archetype',
  'admin.colecoes.lista.pontos': 'weight · spin · grip · break',
  'admin.colecoes.lista.arte': 'art',
  'admin.colecoes.lista.traits': 'traits',
  'admin.colecoes.lista.copiar': 'copy',
  'admin.colecoes.lista.familiaPadrao': 'default family',
  'admin.colecoes.lista.familiaSo': 'No uploaded art: the drawing comes from this family. It changes no attribute.',
  'admin.colecoes.lista.trocarArte': 'replace art',
  'admin.colecoes.lista.mudou': 'differs from approved',
  'admin.colecoes.lista.previa': 'preview',
  'admin.colecoes.lista.editada': 'edited',
  'admin.colecoes.lista.mudouCurto': 'changed',
  'admin.colecoes.lista.antes': 'before: {valor}',
  'admin.colecoes.lista.mostrarMais': { one: 'Show more ({count} left)', other: 'Show more ({count} left)' },
  'admin.colecoes.filtro.raridade': 'any rarity',
  'admin.colecoes.filtro.arquetipo': 'any archetype',
  'admin.colecoes.filtro.familia': 'Art family',
  'admin.colecoes.filtro.trait': 'any trait',
  'admin.colecoes.filtro.semArte': 'without art',
  'admin.colecoes.filtro.busca': 'number or mint',
  'admin.colecoes.filtro.limpar': 'Clear filters',
  'admin.colecoes.trilha.titulo': 'Audit trail',
  'admin.colecoes.trilha.vazia': 'Nothing recorded yet.',
  'admin.colecoes.trilha.assinada': 'signed',
  'admin.colecoes.trilha.sessao': 'session',
  'admin.colecoes.trilha.mensagem': 'message',
  'admin.colecoes.copiado': 'Mint copied.',
  'admin.colecoes.ocupado.lendo': 'Reading…',
  'admin.colecoes.ocupado.assinando': 'Waiting for the wallet signature…',
  'admin.colecoes.ocupado.gerando': 'Saving a new version…',
  'admin.colecoes.ocupado.enviando': 'Sending for review…',
  'admin.colecoes.ocupado.subindo': 'Uploading the art…',
  'admin.colecoes.ok.rascunho': 'Version v{versao} saved as a draft.',
  'admin.colecoes.ok.cadastrada': 'Collection registered as a draft.',
  'admin.colecoes.ok.enviada': 'v{versao} sent for review.',
  'admin.colecoes.ok.aprovada': 'v{versao} approved: the cues count in the game from the next login.',
  'admin.colecoes.ok.recusada': 'v{versao} refused.',
  'admin.colecoes.ok.suspensa': 'Collection suspended: the cues leave the game on the next login.',
  'admin.colecoes.ok.reativada': 'Collection reactivated.',
  'admin.colecoes.erro.sem_carteira': 'Connect the admin wallet in the top bar.',
  'admin.colecoes.carteiraMudou': 'The wallet in the top bar changed: sign in as admin again.',
  'admin.colecoes.entrarCom': 'You will sign with wallet {carteira}, the one connected in the top bar.',
  'admin.colecoes.erro.nao_encontrado': 'The panel did not answer: admin off, IP not on the list, or a wallet that is not an admin.',
  'admin.colecoes.erro.devagar': 'Too many requests in a short time. Wait a minute.',
  'admin.colecoes.erro.sessao': 'The session ended (15 minutes). Sign in again.',
  'admin.colecoes.erro.auditoria_indisponivel': 'The audit trail is down; for safety, nothing was done.',
  'admin.colecoes.erro.das_indisponivel': 'The NFT reader (DAS) did not answer. Try again.',
  'admin.colecoes.erro.assinatura': 'The signature was not accepted (expired, from another wallet or another version). Try again.',
  'admin.colecoes.erro.assinatura_ausente': 'The signature is missing.',
  'admin.colecoes.erro.acao_invalida': 'Invalid action.',
  'admin.colecoes.erro.transicao_invalida': 'The collection is not in a state that allows this. Reload.',
  'admin.colecoes.erro.rascunho_invalido': 'The table was refused (repeated number or too many lines).',
  'admin.colecoes.erro.regras_invalidas': 'The server refused the rules.',
  'admin.colecoes.erro.linhas_invalidas': 'The edited lines were refused (did the collection change?). Reload.',
  'admin.colecoes.erro.modelo_invalido': 'Unknown points model.',
  'admin.colecoes.erro.csv': 'CSV refused.',
  'admin.colecoes.erro.motivo': 'Invalid reason.',
  'admin.colecoes.erro.versao': 'The version under review changed. Reload before deciding.',
  'admin.colecoes.erro.versao_inexistente': 'Version does not exist.',
  'admin.colecoes.erro.suspensa': 'Collection suspended: reactivate it before approving.',
  'admin.colecoes.erro.nada_aprovado': 'There is no approved version to suspend.',
  'admin.colecoes.erro.nao_suspensa': 'The collection is not suspended.',
  'admin.colecoes.erro.colecao_inexistente': 'Collection not registered.',
  'admin.colecoes.erro.colecao_nao_encontrada': 'Collection not found on this network.',
  'admin.colecoes.erro.grupo_vazio': 'No verified NFT in this collection.',
  'admin.colecoes.erro.endereco_invalido': 'Invalid address (base58, 32 to 44 characters).',
  'admin.colecoes.erro.grande_demais': 'File too large.',
  'admin.colecoes.erro.nao_e_png': 'Not a PNG.',
  'admin.colecoes.erro.dimensoes': 'PNG larger than 2048×2048.',
  'admin.colecoes.erro.sem_transparencia': 'PNG without a transparent background.',
  'admin.colecoes.erro.nao_parece_vara': 'The drawing does not look like a cue shaft.',
  'admin.colecoes.erro.numero': 'Invalid number.',
  'admin.colecoes.erro.numero_inexistente': 'Number not in the table.',
  'admin.colecoes.erro.sem_rascunho': 'Generate the table before uploading art.',
  'admin.colecoes.erro.json': 'Malformed request.',
  'admin.colecoes.erro.interno': 'Internal server error.',
  'admin.colecoes.erro.outro': 'The server refused ({codigo}).',


  // ----------------------------------------------------------------- efeito
  'efeito.centro': 'center',

  // --------------------------------------------- status da partida (código)
  'status.quebra': 'Break',
  'status.disputa': 'Lag for the break · round {rodada} of {total}',
  'status.mesa_aberta': 'Open table',
  'livre.status': 'Free mode',
  'livre.quantas': 'Balls',
  'livre.cheia': 'Full rack',
  'livre.repetir': 'Repeat the shot',
  'livre.sortear': 'Shuffle again',
  'livre.voltarNormal': 'Normal game',
  'livre.refazer': 'Redo in free mode',
  'livre.mover': 'Move balls',
  'livre.moverPronto': 'Done',
  'livre.dicaMover': 'Drag any ball to move it. "Done" goes back to aiming.',
  'treino.escolha.titulo': 'How do you want to practice?',
  'treino.escolha.normal': 'Normal game',
  'treino.escolha.normalExplica': 'With rules and a full rack, like a real match.',
  'treino.escolha.livre': 'Free mode',
  'treino.escolha.livreExplica': 'No rules: pick how many balls and repeat the shot as many times as you like.',
  'treino.escolha.mini': 'Mini game',
  'treino.escolha.miniExplica': 'The lag for the break: leave the 1 ball closest to the target and beat your best.',
  'miniJogo.recorde': 'New best: {cm} cm!',
  'miniJogo.ultima': 'Last',
  'miniJogo.melhor': 'Best',
  'miniJogo.caiu': 'pocketed',
  'miniJogo.tentativas': { one: '{count} shot', other: '{count} shots' },
  'miniJogo.sortear': 'Shuffle',
  'miniJogo.sortearDica': 'Draw another position, like the lag in a match',
  'miniJogo.mover': 'Move',
  'miniJogo.moverDica': 'Drag the cue ball, the 1 ball and the target',
  'miniJogo.congelar': 'Freeze',
  'miniJogo.congelarDica': 'Keeps the target still where it is, to practice with a fixed target. Tap again to let it jump',
  'miniJogo.brancaCaiu': 'Foul: the cue ball dropped',
  'miniJogo.falta': 'foul',
  'treino.tipo': 'Practice type',
  'status.na_bola_8': 'On the 8-ball',
  'status.faltam': { one: '{n} ball left', other: '{n} balls left' },
  'status.venceu': 'Player {jogador} wins',
  'status.mesa_vazia': 'Empty table',
  'status.bola_da_vez': 'Ball on: {bola}',
  'bola.1': 'red',
  'bola.2': 'yellow',
  'bola.3': 'green',
  'bola.4': 'brown',
  'bola.5': 'blue',
  'bola.6': 'pink',
  'bola.7': 'black',

  // ------------------------------------------------------------ julgamento
  'falta.generica': 'Foul.',
  'falta.cue-ball-pocketed': 'Cue ball pocketed.',
  'falta.cue-ball-off-table': 'Cue ball off the table.',
  'falta.ball-off-table': 'Ball off the table.',
  'falta.no-contact': 'No ball was hit.',
  'falta.wrong-ball-first': 'Wrong ball hit first.',
  'falta.eight-ball-first-illegal': 'Cannot hit the 8-ball yet.',
  'falta.no-rail-after-contact': 'No ball reached a cushion.',
  'falta.no-call': 'Pocket was not called.',
  'falta.wrong-ball-called': 'Pocketed a different ball.',
  'falta.free-ball-missed': 'Missed the free ball.',
  'falta.wrong-ball-potted': 'Pocketed a ball that was not the ball on.',
  'julgamento.pontos': '+{n} points',

  'disputa.titulo': 'Lag for the break',
  'disputa.explicacao': 'Leave the colored ball as close as you can to the center of the target. Whoever gets closer breaks.',
  'disputa.rodada': 'Round {n} of {total}',
  'disputa.resultado': '{cm} cm from the target',
  'disputa.caiu': 'The ball dropped into a pocket',
  'disputa.semTacada': 'No shot in time',
  'disputa.empate': 'Tie! New round, with the order reversed.',
  'disputa.voceEstoura': 'You won the lag and break!',
  'disputa.adversarioEstoura': 'Your opponent won the lag and breaks.',
  'disputa.jogadorEstoura': 'Player {n} won the lag and breaks.',
  'disputa.marca.voce': 'you',
  'disputa.marca.adversario': 'opponent',
  'disputa.marca.jogador': 'P{n}',
  'julgamento.segue': 'Pocketed — stays at the table.',

  // ------------------------------------------------------- mesa (controle)
  'partida.limiteTacadas': 'Replay shot limit reached.',
  'partida.limiteDeclaracoes': 'Replay call limit reached.',
  'partida.limitePosicionamentos': 'Replay placement limit reached.',
  'partida.desync': 'table diverged from the server ({nosso} ≠ {servidor})',

  // -------------------------------------------------------------------- hud
  'hud.vezDoJogador': "Player {n}'s turn",
  'hud.dica.escolha': 'Pick one of the options on the table',
  'hud.dica.bolaNaMao': 'Drop the cue ball where you want to play',
  'hud.dica.declare': 'Call the pocket before shooting',
  'hud.dica.arraste': 'Drag from the cue ball and release to shoot',

  // ----------------------------------------------------------------- placar
  'placar.voce': 'You',
  'placar.adversario': 'Opponent',
  'placar.jogador1': 'Player 1',
  'placar.jogador2': 'Player 2',
  'placar.faltam': { one: 'left', other: 'left' },
  'placar.pontos': { one: 'point', other: 'points' },
  'placar.soA8': 'just the 8',
  'placar.mesaAberta': 'open table',
  'placar.naDisputa': 'lag for the break',
  'placar.naVez': 'to shoot',
  'placar.suaVez': 'your turn',
  'placar.venceu': 'won',

  // ---------------------------------------------------------- cartão do fim
  'cartao.venceuJogador': 'Player {n} won',
  'cartao.vencedor': 'winner',
  'cartao.treino': 'Practice table',
  'cartao.mesaApostada': 'Staked table',
  'cartao.duracao': 'Duration',
  'cartao.tacadas': 'Shots',
  'cartao.aposta': 'Stake',
  'cartao.apostaValor': '{valor} {simbolo} each · pot {pote} {simbolo}',
  'cartao.encacapadas': 'pocketed',
  'cartao.compartilhar': 'Share result',
  'cartao.copiar': 'Copy image',
  'cartao.baixar': 'Download image',
  'cartao.copiada': 'Image copied!',
  'cartao.baixada': 'Image downloaded.',
  'cartao.falhou': 'Could not generate the image.',
  'cartao.rodape': 'Played on SOL Pool · deterministic physics, replay on the blockchain',
  'duracao.segundos': '{s}s',
  'duracao.minutos': '{min} min {s}s',
  'duracao.horas': '{h} h {min} min',
  'duracao.dias': '{d}d {h}h',
  'decisao.jogadorEscolhe': 'Player {n} chooses',
  'decisao.voceEscolhe': 'You choose',
  'decisao.recomendado': '{opcao} (recommended)',
  'cacapa.toque': 'Tap the pocket the 8-ball will drop into',
  'cacapa.toqueBola': 'Tap the pocket your ball will drop into',
  'cacapa.declarada': 'called',

  // ------------------------------------------------------------------- mesa
  'mesa.ajusteFino': '◎ Fine aim',
  'mesa.ajusteFinoAjuda':
    'Moves the aim five times slower, to pick the exact spot (or hold Shift)',
  'mesa.travarMira': '🔒 Lock aim',
  'mesa.travarMiraAjuda':
    'Z — freezes the angle: dragging only changes power. Stays locked between shots.',
  'mesa.som': 'Sound',
  'mesa.mudo': 'Muted',
  'mesa.ligarSom': 'Turn sound on',
  'mesa.silenciar': 'Mute',
  'mesa.novaPartida': 'New game',
  'mesa.pote': 'Pot {valor} {simbolo}',
  'mesa.modalidade': 'Game',
  'mira.rotulo': 'Aim',
  'treino.taco': 'Test cue',
  'treino.tacoPadrao': 'Game default',
  'mesa.voltar': 'Back',
  'mesa.lobby': '← Lobby',
  'mesa.desistir': 'Forfeit',
  'mesa.conectando': 'connecting…',

  // ----------------------------------------------------------- mesa em rede
  'net.semCarteiraTitulo': 'No wallet found',
  'net.semCarteiraTexto':
    'Install a Solana wallet — Phantom, Solflare, Backpack and others work — and reload to play a wagered table.',
  'net.voltarAoLobby': 'Back to the lobby',
  'net.travou.titulo': 'Something went wrong starting the match',
  'net.travou.motivo': 'Reason: {motivo}',
  'net.travou.semMotivo':
    'The break was not agreed. Your opponent may have closed the page, or opened the table in another browser.',
  'net.travou.volta':
    'Your stakes come back on their own in {contador}. Nothing to do: you can go back to the lobby or close this page.',
  'net.travou.vencido':
    'The table deadline has passed: the server is refunding the stakes. It may take a few minutes.',
  'net.travou.desconhecido': 'Your stakes come back on their own when the table deadline passes.',
  'net.demo': 'DEMO — local table, no wallet and no stake. Only this page’s screen.',
  'net.confirmarDesistir': 'Forfeiting hands the pot to your opponent. Confirm?',
  'net.etapa.aguardando': 'Find an opponent',
  'net.etapa.comprometendo': 'Agree on the break',
  'net.etapa.revelando': 'Reveal the break',
  'net.explicacao.aguardando':
    'Your table is open and the deposit is already locked in the contract. The match starts as soon as someone joins.',
  'net.explicacao.comprometendo':
    'You and your opponent are drawing the break together: each sends a sealed number, and neither of you — nor the server — can pick the outcome alone.',
  'net.explicacao.revelando':
    'Both numbers have been opened. The table is being racked with the seed they form.',
  'net.preparando': 'Preparing the table',
  'net.sessao.titulo': 'Sign in to get back to the table',
  'net.sessao.texto':
    'Your session is no longer valid — the server restarted or it expired. Sign the login and the match resumes where it stopped.',
  'net.sessao.assinar': 'Sign login',
  'net.sessao.aguardandoCarteira': 'Waiting for the wallet…',
  'net.adversarioFora': 'Opponent offline — their turn passes on its own',
  'net.fase.aguardando': 'Waiting for a table to close in the lobby…',
  'net.fase.comprometendo': 'Agreeing on the break…',
  'net.fase.revelando': 'Revealing the break…',
  'net.suaVez': 'Your turn',
  'net.vezDoAdversario': "Opponent's turn",
  'net.voceVenceu': 'You won',
  'net.vocePerdeu': 'You lost',
  'net.partidaAnulada': 'Match voided',
  'net.por': 'by {motivo}',
  'net.desyncTitulo': 'The table went out of sync with the server.',
  'net.desyncTexto':
    'The match result is still correct and will be settled normally. Reload to follow along again.',
  'net.cliqueCacapa': 'Click the pocket the 8-ball will go into.',
  'net.adversarioNaBola8': 'Opponent is on the 8-ball…',
  'net.escolhaCacapa': 'Your turn: pick the pocket for this shot to unlock the cue.',
  'net.adversarioEscolhendoCacapa': 'Opponent is picking a pocket…',
  'net.escolhaNaMesa': 'Choose on the table.',
  'net.adversarioEscolhendo': 'Opponent is choosing…',
  'net.entradasVoltam': 'Both stakes are returned by the contract.',
  'net.enviandoLiquidacao': 'Sending the payout and the replay to the blockchain…',
  'net.pagamentoGravado': 'Payout and replay recorded.',
  'net.verTransacao': 'view the transaction ↗',
  'net.naoLiquidada':
    'Could not settle right now ({motivo}). Stakes are returned at the contract deadline.',
  'net.motivoDesconhecido': 'unknown reason',
  'net.combinandoQuebra': 'Agreeing on the break with your opponent…',
  'net.partidaRetomada': 'Match resumed.',
  'net.adversarioDesconectou': 'Opponent disconnected — their turns are passing.',
  'net.adversarioVoltou': 'Opponent is back.',

  // ------------------------------------------------ cartão do jogador (mesa)
  'cartaoJogador.fechar': 'Close',
  'cartaoJogador.carregando': 'Loading stats…',
  'cartaoJogador.indisponivel': 'Stats are not available on this server.',
  'cartaoJogador.erro': 'Could not load the stats.',
  'cartaoJogador.novato': 'First recorded match: no history yet.',
  'cartaoJogador.aproveitamento': 'win rate',
  'cartaoJogador.vd': 'W–L',
  'cartaoJogador.partidas': 'matches',
  'cartaoJogador.ultimas': 'Last',
  'cartaoJogador.v': 'W',
  'cartaoJogador.d': 'L',
  'cartaoJogador.semana': 'Week',
  'cartaoJogador.posicao': '#{n}',
  'cartaoJogador.pontos': '{pontos} pts',

  // ------------------------------------------------ formatos e eventos de mesa
  'formato.titulo': 'Format',
  'formato.unica': 'Single match',
  'formato.unicaAjuda': 'With a stake, as always.',
  'formato.melhorDe3': 'Best of 3',
  'formato.melhorDe3Ajuda': 'Friendly · first to win 2 matches.',
  'formato.torneio': 'Tournament (4 players)',
  'formato.unicaCurto': '1v1',
  'formato.torneioCurto': 'Tournament',
  'formato.emTestes': 'in testing',
  'formato.avisoInicio': 'Open now: ',
  'formato.avisoTorneios': { one: '{count} tournament', other: '{count} tournaments' },
  'formato.avisoSeries': { one: '{count} best of 3', other: '{count} best of 3 series' },
  'formato.avisoE': ' and ',
  'formato.verTorneios': 'See tournaments',
  'formato.verSeries': 'See best of 3',
  'formato.emTestesAviso': 'This format is still in testing: it may break or change without notice. 1v1 is the stable mode.',
  'formato.umAberto': '1 open',
  'formato.abertos': '{n} open',
  'formato.torneioAjuda': 'Friendly · semifinals and final.',
  'eventoMesa.amistoso': 'friendly',
  'eventoMesa.amistosoAviso': 'Friendly: no stakes for now. Once in, you play to the end; tournament losers can watch.',
  'eventoMesa.nome': 'Event name',
  'eventoMesa.entradaRotulo': 'Tournament entry',
  'eventoMesa.entradaRotuloSerie': 'Best-of-3 entry',
  'eventoMesa.entradaFaixaSerie':
    'Charged once from each of the two players, on joining. From {min} to {max} {simbolo}.',
  'eventoMesa.itemVencedorLeva': 'Whoever wins the series takes {valor}',
  'eventoMesa.custoDeAbrirASerie':
    'Opening the room charges YOUR entry plus {valor} in rent for the account on-chain, in a single signature — you play the series, you are not just organising it. The rent stays in the account and is not refunded today.',
  'eventoMesa.entradaFaixa': 'Charged once, when you sign up. From {min} to {max} {simbolo}.',
  'eventoMesa.entradaIndisponivel': 'Paid tournaments are unavailable right now: friendly only.',
  'eventoMesa.campeaoLeva': 'The champion takes {valor}',
  'eventoMesa.pagamentoFeito': 'Paid: {valor} SOL to {quem}',
  'eventoMesa.pagamentoFeitoSemValor': 'Prize paid on-chain to {quem}',
  'eventoMesa.pagamentoVoce': 'you',
  'eventoMesa.pagamentoACaminho': 'Prize payment being processed on-chain…',
  'eventoMesa.pagamentoCancelado': 'Tournament cancelled: each player can withdraw their entry',
  'eventoMesa.entradaDoPainel': '{valor} SOL entry',
  'eventoMesa.custoDeAbrirASala':
    'Opening the room costs {valor} in rent for the tournament account, charged to you when you sign. This is not an entry fee: whoever creates does not play. That amount stays in the account and is not refunded today.',
  'eventoMesa.assinandoCriacao': 'Confirm in your wallet to open the tournament…',
  'eventoMesa.assinandoInscricao': 'Confirm in your wallet to sign up…',
  'eventoMesa.cancelarTorneio': 'Cancel and refund',
  'eventoMesa.sacarInscricao': 'Withdraw my entry',
  'eventoMesa.cancelando': 'Cancelling the tournament and refunding entries…',
  'eventoMesa.cancelado': 'Tournament cancelled. Each player can withdraw their entry.',
  'eventoMesa.sacado': 'Entry refunded to your wallet.',
  'eventoMesa.criar': 'Create event',
  'eventoMesa.naLista': 'Friendly',
  'eventoMesa.entradaNaLista': '{valor} {simbolo} · pot {pote}',
  'eventoMesa.vagas': '{n} of {total} players',
  'eventoMesa.fechaEm': 'closes in {tempo}',
  'eventoMesa.entrar': 'Join',
  'eventoMesa.assistir': 'Watch',
  'eventoMesa.assistirOutras': 'Watch the remaining matches',
  'eventoMesa.sair': 'Leave event',
  'eventoMesa.irParaMesa': 'Go to the table',
  'eventoMesa.fechar': 'Close',
  'eventoMesa.jogando': 'in play',
  'eventoMesa.jogadores': 'Players',
  'eventoMesa.convite': 'Invite (share the link)',
  'eventoMesa.conviteComSenha': 'This event has a password: send it along with the link.',
  'eventoMesa.senhaExplica': 'Only those who know the password can join. It is not stored anywhere.',
  'eventoMesa.vozExplica': 'Talk over voice during the matches.',
  'eventoMesa.etapaUnica': 'Single step · no deposit',
  'eventoMesa.placeholderSerie': 'E.g.: Friday rematch',
  'eventoMesa.placeholderTorneio': 'E.g.: Friends tournament',
  'eventoMesa.comoFunciona': 'How it works',
  'eventoMesa.jogadoresSerie': '2',
  'eventoMesa.jogadoresTorneio': '4',
  'eventoMesa.partidas': 'Matches',
  'eventoMesa.partidasSerie': 'up to 3 · first to win 2',
  'eventoMesa.partidasTorneio': '3 · two semifinals and the final',
  'eventoMesa.aposta': 'Stake',
  'eventoMesa.semAposta': 'none · friendly',
  'eventoMesa.plateia': 'Audience',
  'eventoMesa.plateiaAberta': 'Anyone can watch',
  'criar.configuracao': 'Match setup',
  'criar.opcoes': 'Match options',
  'criar.resumo': 'Stake summary',
  'criar.vocePaga': 'You pay',
  'criar.poteTotal': 'Total pot',
  'criar.prazosDaPartida': 'Match timers',
  'criar.opcional': '(optional)',
  'criar.senhaFaixa': '{min} to {max} characters.',
  'criar.subtitulo.unica': 'Single match with a stake. Once in, you play to the end.',
  'criar.subtitulo.melhor-de-3': 'Best of 3 matches. Friendly, no stake.',
  'criar.subtitulo.torneio': '4-player tournament. Friendly, semifinals and final.',
  'formato.emTestesCurto': 'This format is still in testing: it may break or change without notice.',
  'eventoMesa.amistosoCurto': 'Friendly: no stake in this phase.',
  'eventoMesa.configuracaoTorneio': 'Tournament setup',
  'eventoMesa.nomeTorneio': 'Tournament name',
  'eventoMesa.formatoTorneio': 'Tournament format',
  'eventoMesa.sequencia': 'Match sequence',
  'eventoMesa.partida1': '1st match',
  'eventoMesa.partida2': '2nd match',
  'eventoMesa.partida3': '3rd match (if needed)',
  'eventoMesa.semi1': 'Semi 1',
  'eventoMesa.semi2': 'Semi 2',
  'eventoMesa.jogadorN': 'Player {n}',
  'eventoMesa.vencedoresDasSemis': 'Semifinal winners',
  'eventoMesa.chaveDescricao': 'Semifinal 1: players 1 and 2. Semifinal 2: players 3 and 4. The winners play the final.',
  'eventoMesa.itemJogadoresSerie': '2 players',
  'eventoMesa.itemPartidasSerie': 'Up to 3 matches',
  'eventoMesa.itemVenceSerie': 'First to win 2',
  'eventoMesa.itemJogadoresTorneio': '4 players',
  'eventoMesa.itemPartidasTorneio': '2 semifinals and 1 final',
  'eventoMesa.itemSemAposta': 'No stake · friendly',
  'eventoMesa.itemEliminadoAssiste': 'Knocked-out players can watch',
  'eventoMesa.estado.aberto': 'Waiting',
  'eventoMesa.estado.jogando': 'In progress',
  'eventoMesa.estado.terminado': 'Finished',
  'eventoMesa.estado.desfeito': 'Dropped',
  'eventoMesa.vagaLivre': 'Waiting for a player…',
  'eventoMesa.criador': 'Host',
  'eventoMesa.conviteTitulo': 'Invite',
  'eventoMesa.conviteExplica': 'Send the link to whoever will play: it opens straight into this event.',
  'eventoMesa.plateiaFechada': 'Closed audience: players only',
  'eventoMesa.plateiaFechadaTorneio': 'Closed audience: the 4 players only',
  'eventoMesa.plateiaFechadaCartao': 'Closed to audience',
  'eventoMesa.plateiaExplicaSerie': 'Anyone can watch the series.',
  'eventoMesa.plateiaExplicaTorneio': 'Anyone can watch the tournament.',
  'eventoMesa.comoFuncionaSerie': 'Starts when the 2nd player joins. If nobody joins within 10 minutes, it is dropped.',
  'eventoMesa.comoFuncionaTorneio': 'Starts when all 4 join: both semifinals run together. If it does not fill within 10 minutes, it is dropped.',
  'eventoMesa.prazoDuracaoPartida': 'Per match',
  'eventoMesa.voce': 'You',
  'eventoMesa.esperando': 'Waiting for players: {n} to go.',
  'eventoMesa.jogandoAgora': 'Your match is in play.',
  'eventoMesa.proximaQuebra': 'The next match starts in a moment: {nome} breaks.',
  'eventoMesa.proximaEmInstantes': 'The next match starts in a moment.',
  'eventoMesa.naFinal': 'You are in the final! Waiting for the other semifinal.',
  'eventoMesa.eliminado': 'You were knocked out. You can watch the remaining matches.',
  'eventoMesa.voceCampeao': 'You are the champion!',
  'eventoMesa.campeao': '{nome} is the champion!',
  'eventoMesa.dividido': 'The final got stuck: {a} and {b} share the title.',
  'eventoMesa.semCampeao': 'The event ended without a champion ({motivo}).',
  'eventoMesa.desfeito': 'The event was called off ({motivo}).',
  'eventoMesa.motivo.todos_sairam': 'everyone left',
  'eventoMesa.motivo.partida_travou': 'a match in the series got stuck',
  'eventoMesa.motivo.final_travou': 'the final got stuck',
  'eventoMesa.motivo.semifinal_travou': 'a semifinal got stuck',
  'eventoMesa.motivo.nao_fechou': 'it did not fill up in time',
  'eventoMesa.motivo.passou_do_tempo': 'the event ran past its time limit',
  'eventoMesa.motivo.cancelado_pelo_dono': 'Cancelled by its creator',
  'eventoMesa.semifinal': 'Semifinal {n}',
  'eventoMesa.final': 'Final',

  // ------------------------------------------------------------- espectador
  'net.assistindo': 'Watching',
  'net.assistindoAguardando': 'Watching — waiting for the match to start…',
  'net.vezDoJogador': "Player {n}'s turn",
  'net.jogadorVenceu': 'Player {n} won',
  'net.jogadorNaBola8': 'Player {n} is on the 8-ball…',
  'net.jogadorEscolhendoCacapa': 'Player {n} is picking a pocket…',
  'net.jogadorEscolhendo': 'Player {n} is choosing…',
  'net.plateia': { one: '{count} watching', other: '{count} watching' },
  'net.naoDaParaAssistir': 'Cannot watch',
  'net.senhaTitulo': 'This table has a password',
  'net.senhaExplica': 'To watch, type the password the table creator shared with you.',
  'net.senhaEnviar': 'Watch',

  // --------------------------------------------------------------- conversa
  'chat.titulo': 'Chat',
  'chat.placeholder': 'Say something to the table…',
  'chat.placeholderDemo': 'No server in demo mode',
  'chat.enviar': 'Send',
  'chat.rapidas': 'Quick phrases',
  'chat.rapida.boaSorte': 'Good luck!',
  'chat.rapida.boaJogada': 'Nice shot!',
  'chat.rapida.quase': 'So close!',
  'chat.rapida.valeu': 'Thanks!',
  'chat.rapida.gg': 'GG',
  'chat.rapida.oi': 'Hi!',
  'chat.rapida.queTacada': 'What a shot!',
  'chat.rapida.revanche': 'Rematch?',
  'chat.rapida.hojeNao': 'Not today!',
  'chat.rapida.sorteDePrincipiante': "Beginner's luck",
  'chat.rapida.muitoFraco': 'Too soft!',
  'chat.rapida.muitoForte': 'Too hard!',
  'chat.maisFrases': 'More phrases',
  'chat.rapida.bomJogo': 'Have fun!',
  'chat.rapida.ateAProxima': 'See you next time!',
  'chat.rapida.tchau': 'Bye!',
  'chat.rapida.mandouBem': 'Well played!',
  'chat.rapida.jogouMuito': "You're on fire!",
  'chat.rapida.queAula': 'Masterclass!',
  'chat.rapida.taNervoso': 'Nervous?',
  'chat.rapida.pressao': 'Pressure!',
  'chat.rapida.suaVez': 'Your turn!',
  'chat.rapida.pensando': 'Thinking…',
  'chat.rapida.jaVolto': 'Be right back',
  'chat.rapida.foiMal': 'My bad!',
  'chat.aba.saudar': 'Greet',
  'chat.aba.elogiar': 'Praise',
  'chat.aba.provocar': 'Tease',
  'chat.aba.avisos': 'Notes',
  'chat.vazio': 'Nobody has said anything yet.',
  'chat.voce': 'you',
  'chat.adversario': 'opponent',
  'chat.jogador': 'player {n}',
  'chat.naSala': 'The conversation starts here and continues at the table.',

  // ------------------------------------------------------------ profile
  'perfil.rotulo': 'Nickname',
  'perfil.placeholder': 'What should we call you?',
  'perfil.salvar': 'Save',
  'perfil.salvando': 'Saving…',
  'perfil.salvo': 'Nickname saved.',
  'perfil.explica':
    'Shown instead of the address — always with it alongside, because nicknames are not unique.',

  // ------------------------------------------------------------ modalidades
  'modo.eightball.nome': '8-Ball',
  'modo.eightball.descricao': 'Solids against stripes. Clear your group and pocket the 8 to win.',
  'modo.sinuca.nome': 'Brazilian sinuca',
  'modo.sinuca.descricao': 'Seven colours in ascending order. Most points wins.',

  // ------------------------------------------------------- regras (página)
  'regras.tituloPagina': 'SOL Pool — Rules',
  'regras.cabecalho': 'Rules',
  'regras.intro':
    'Everything on this page is read from the code that judges the matches: the rule sets, every foul and every way to lose, the deadlines and the limits. There is no loose text to go stale — what is here is what the server enforces.',
  'regras.fisica':
    'Physics v{versao}, digest <code>{digest}</code>. The same shot produces the same table in any browser; the <a href="/determinism">determinism page</a> proves it in yours.',
  'regras.bolas': { one: '{count} object ball', other: '{count} object balls' },

  'regras.secao.modalidades': 'Game modes',
  'regras.secao.conjunto': 'Rule set in use',
  'regras.secao.comparacao': 'In use, WPA and bar rules, side by side',
  'regras.secao.faltas': 'Fouls',
  'regras.secao.fins': 'How a match ends',
  'regras.secao.decisoes': 'Decisions after the break',
  'regras.secao.prazos': 'Deadlines',
  'regras.secao.limites': 'Replay limits',

  'regras.col.regra': 'rule',
  'regras.col.valor': 'value',
  'regras.col.oQueSignifica': 'what it means',
  'regras.col.emUso': 'in use',
  'regras.col.wpa': 'WPA',
  'regras.col.bar': 'bar',
  'regras.col.consequencia': 'consequence',
  'regras.sim': 'yes',
  'regras.nao': 'no',
  'regras.iguais': 'The rule set in use is identical to the WPA one.',
  'regras.diferencas': {
    one: '{count} difference from the WPA, highlighted.',
    other: '{count} differences from the WPA, highlighted.',
  },

  'regras.campo.callShot': 'When ball and pocket must be called before the shot.',
  'regras.campo.callShot.always': 'on every shot',
  'regras.campo.callShot.eight-only': 'only on the 8-ball',
  'regras.campo.callShot.never': 'never',
  'regras.campo.callShot.pocket': 'the pocket, on every shot',
  'regras.campo.breakMinBallsToRail':
    'How many object balls must reach a cushion for the break to count, if none is pocketed. Below that the break is illegal and the opponent chooses what to do.',
  'regras.campo.breakMinBallsToRail.valor': {
    one: '{count} ball to a cushion',
    other: '{count} balls to a cushion',
  },
  'regras.campo.kitchenAfterBreakScratch':
    'A foul on the break restricts ball in hand to the kitchen (behind the head string). Off the break, ball in hand is always anywhere.',
  'regras.campo.eightOnBreakLoses':
    'Pocketing the 8 on the break loses the match. When not, the breaker chooses between spotting the 8 and playing on, or re-breaking.',
  'regras.campo.threeFoulRule':
    'Three consecutive fouls by the same player hand over the match. No warning: the foul count is on both screens the whole time.',

  'regras.sinuca.foulPoints': 'Points the opponent gets on every foul.',
  'regras.sinuca.foulPoints.valor': { one: '{count} point', other: '{count} points' },
  'regras.sinuca.freeBallAfterPot':
    'After pocketing the ball on, the player may nominate a free ball. Hit it and score; miss it and take the penalty.',
  'regras.sinuca.endOnUnreachableScore':
    'The match ends once the score gap exceeds what is left on the table — it does not drag on to the last ball.',
  'regras.sinuca.ordem':
    'The ball on is always the lowest still on the table; each ball is worth its own number. A ball pocketed illegally goes back on the table. If the table empties on a tie, the black is respotted and decides the match, with the non-breaker shooting.',

  'regras.falta.cue-ball-pocketed': 'The cue ball dropped in a pocket.',
  'regras.falta.cue-ball-off-table': 'The cue ball jumped off the table.',
  'regras.falta.ball-off-table':
    'An object ball jumped off the table. In 8-Ball it does not come back; in sinuca it is respotted.',
  'regras.falta.no-contact':
    'The cue ball touched no ball at all. It is also what the clock records when the shot deadline runs out.',
  'regras.falta.wrong-ball-first':
    'The first ball touched was not a legal target: one of the opponent\'s group in 8-Ball, or one that was not the ball on in sinuca.',
  'regras.falta.eight-ball-first-illegal':
    'The cue ball hit the 8 first before the player cleared their group. On an open table it is a foul too — "open" does not mean anything goes.',
  'regras.falta.no-rail-after-contact':
    'After contact, no ball was pocketed and none reached a cushion. It is what stops parking the cue ball without trying anything.',
  'regras.falta.no-call': 'The shot required calling ball and pocket, and nothing was called.',
  'regras.falta.free-ball-missed': 'The player nominated a free ball and did not hit it first.',
  'regras.falta.wrong-ball-potted':
    'A ball that was neither the ball on nor the nominated free ball dropped. It goes back on the table, but the foul counts.',

  'regras.efeito.bolaNaMao': 'Ball in hand for the opponent, anywhere on the table.',
  'regras.efeito.bolaNaMaoCozinha': 'If the foul was on the break, ball in hand is restricted to the kitchen.',
  'regras.efeito.contaTres': 'Counts towards the three-consecutive-fouls rule.',
  'regras.efeito.naBola8': 'Pocketing the 8 on the same shot loses the match.',
  'regras.efeito.pontos': {
    one: '{count} point to the opponent.',
    other: '{count} points to the opponent.',
  },
  'regras.efeito.voltam': 'Balls pocketed on the shot go back on the table; the turn passes.',
  'regras.efeito.brancaNaMao': 'The opponent gets the cue ball in hand.',
  'regras.efeito.brancaFica': 'The cue ball stays where it stopped.',

  'regras.fim.eight-ball-potted':
    'Win: the group was clear and the 8 dropped in the called pocket, without a foul.',
  'regras.fim.eight-ball-early': 'Loss: the 8 dropped before the player cleared their group.',
  'regras.fim.eight-ball-wrong-pocket':
    'Loss: the 8 dropped, but not in the called pocket — or the called ball was not the 8.',
  'regras.fim.eight-ball-with-foul': 'Loss: the 8 dropped on the same shot as a foul.',
  'regras.fim.eight-ball-off-table': 'Loss: the 8 jumped off the table.',
  'regras.fim.three-fouls': 'Loss: third consecutive foul by the same player.',
  'regras.fim.forfeit':
    'Forfeit: whoever forfeits loses. Only after the break reveal; before that there is no match to forfeit.',
  'regras.fim.all-balls-potted': 'The table is empty: most points wins.',
  'regras.fim.score-unreachable':
    'The score gap exceeded what is left on the table: whoever is ahead wins.',
  'regras.vitoria': 'win',
  'regras.derrota': 'loss',
  'regras.placar': 'whoever is ahead',

  'regras.decisao.eight-on-break':
    'The 8 dropped on the break. Neither a foul nor a loss: the breaker chooses. If they also fouled on the shot, the opponent chooses instead.',
  'regras.decisao.illegal-break':
    'The break pocketed nothing and sent too few balls to a cushion. The opponent chooses — and, if there was a foul on the shot, also gets ball in hand.',
  'regras.decisao.opcoes': 'options',
  'regras.decisao.semResposta': 'With no answer in time, the first option applies.',

  'regras.prazo.tacada': 'Shot clock',
  'regras.prazo.tacadaExplica':
    'When it runs out, the turn passes as a zero-power shot: a foul for <a href="#falta-no-contact">hitting no ball</a>. Ball in hand not yet placed goes to the cue spot. There is no walkover: whoever is gone loses turns, and the game ends by the rules.',
  'regras.prazo.revelacao': 'Break reveal',
  'regras.prazo.revelacaoExplica':
    'Both players reveal the secret that draws the break. If one fails to reveal in time, the match is voided and the stakes return through the contract.',
  'regras.prazo.duracao': 'Maximum duration',
  'regras.prazo.duracaoExplica':
    'Counted from the start of the match. When it runs out, the match ends with a winner: whoever is ahead. It stays below the on-chain deadline so settlement still fits inside it.',
  'regras.prazo.onChain': 'Contract deadline',
  'regras.prazo.onChainExplica':
    'After it, either player can claim the deposit back. It is the safety net if the server disappears — a normal match ends well before.',
  'regras.prazo.desconexao':
    'Dropping from the match is not forfeiting: the absent player\'s turns pass, and whoever comes back plays from where things stand. With both gone, the clock stops.',
  'regras.naFrente':
    'Who is ahead: in 8-Ball, whoever has fewer of their own balls left on the table; in sinuca, whoever has more points. On a tie, the breaker loses — the break is the advantage of opening the table.',

  'regras.limite.tacadas': 'shots',
  'regras.limite.posicoes': 'ball-in-hand placements',
  'regras.limite.declaracoes': 'pocket calls',
  'regras.limite.decisoes': 'decisions',
  'regras.limite.explica':
    'Every match is recorded as a replay that fits in the settlement transaction, so it has a ceiling. When the space runs out, the match ends with a winner: whoever is ahead. Voiding it would reward stalling.',
  'regras.fimServidor.regras': 'Ended by the rules: someone pocketed the 8 or closed out the score.',
  'regras.fimServidor.desistência': 'A player forfeited.',
  'regras.fimServidor.tempo':
    'The maximum duration ran out (whoever is ahead wins) or the reveal deadline did (voided).',
  'regras.fimServidor.replay cheio': 'The replay filled up; whoever is ahead wins.',
  'regras.segundos': '{n} s',
  'regras.minutos': '{n} min',

  // --------------------------------------------------------------- carteira
  'carteira.recusada': 'Request declined in the wallet.',
  'carteira.foraDaRede': 'not on the {rede} network',
  'carteira.soEnviando': 'only signs by broadcasting through its own network',
  'carteira.semAssinatura': 'The wallet did not return a signature.',
  'carteira.semTransacao': 'The wallet did not return the signed transaction.',

  // ------------------------------------------------------------------ saldo
  'saldo.criar': 'create this table',
  'saldo.entrar': 'join this table',
  'saldo.insuficiente':
    'Insufficient balance to {oQue}: the stake is {aposta} SOL and you have {tem}. Including the network fee, about {necessario} SOL would be needed.',

  // -------------------------------------------------------------- changelog
  /* O MESMO texto do menu: quem clica em "What's new" tem de aterrissar em
     "What's new". Palavras diferentes para a mesma coisa fazem o leitor
     duvidar se chegou onde queria — e "News" ainda sugere notícias, que é
     outra coisa. */
  'changelog.tituloPagina': 'SOL Pool — What’s new',
  'changelog.titulo': 'What’s new',
  'changelog.subtitulo': 'What changed in the game, in order — from most recent to oldest.',
  'changelog.selo.rotulo': 'replay v{replay}',
  'changelog.selo.tituloAttr':
    'This version changed the rules: matches recorded from it on use format {replay}.',

  // Physics v7: the break stutter a player spotted and slow motion exposed.
  'changelog.a-quebra-sem-travadinha.nome': 'The break without the stutter',
  'changelog.a-quebra-sem-travadinha.resumo':
    'The table no longer freezes for a moment after the cue ball hits the rack — and every ball settles a little further out.',
  'changelog.a-quebra-sem-travadinha.mudanca.0.o':
    'On the break, the whole table used to freeze for a moment right after contact and then shoot off. Fixed.',
  'changelog.a-quebra-sem-travadinha.mudanca.0.porque':
    'The physics threw away the rest of the step after every contact: the balls had speed, but time stopped moving for all of them. On the break, with contacts cascading, that meant a dozen frozen steps in a row.',
  'changelog.a-quebra-sem-travadinha.mudanca.1.o':
    'Balls settle SOMEWHERE ELSE: on a full-power break, 18 cm on average from where they used to stop; on a half-power shot, 15 cm; on a soft one, 4 cm. The break also pots more — from 1.4 to 1.7 balls.',
  'changelog.a-quebra-sem-travadinha.mudanca.1.porque':
    'That is the time coming back. Each contact lost up to 1/240 of a second, and it added up — which is why no shot comes out the same as before, not even the soft ones. It is not that a ball travels much further: the path changes, and so does where it ends up.',
  'changelog.a-quebra-sem-travadinha.mudanca.2.o':
    'Matches played before this version still check out, under the physics that produced them.',
  'changelog.a-quebra-sem-travadinha.mudanca.2.porque':
    'Every physics version is kept. The replay records which one it was played on, and that is the one that verifies it — otherwise an honest past match would start looking rigged.',

  // The training mini game, and the aim that now applies from the first frame.
  'changelog.mini-jogo-no-treino.nome': 'Mini game in training',
  'changelog.mini-jogo-no-treino.resumo':
    'The lag for the break became a solo training mode, with a target that moves — and your chosen aim now applies from the very first shot.',
  'changelog.mini-jogo-no-treino.mudanca.0.o':
    'A third training mode: Mini game. The same lag for the break as in a match, with your last and best distance on the bar.',
  'changelog.mini-jogo-no-treino.mudanca.0.porque':
    'The layout comes from the same draw as the match and the distance from the same maths: what you practise here is what shows up on a staked table.',
  'changelog.mini-jogo-no-treino.mudanca.1.o':
    'The target moves every 3 seconds, continuously — including while you aim. The lit target when ball 1 stops is the one that counts.',
  'changelog.mini-jogo-no-treino.mudanca.1.porque':
    'With a fixed target the lag is only about power. The 3 seconds came from measuring 2,700 shots: each target matches a type of shot, so timing it is a choice rather than luck. The Freeze button holds the target still.',
  'changelog.mini-jogo-no-treino.mudanca.2.o':
    'In the training mini game, potting the cue ball is a foul, like potting ball 1.',
  'changelog.mini-jogo-no-treino.mudanca.2.porque':
    'Letting the cue ball drop for free made the hardest shot always worth it. This applies to training only, for now.',
  'changelog.mini-jogo-no-treino.mudanca.3.o':
    'The aim you picked applies from the first frame of the mini game and of the break.',
  'changelog.mini-jogo-no-treino.mudanca.3.porque':
    'Each phase of a match builds the table again, and it used to start with the normal aim until the next message from the server. Anyone shooting early saw the wrong line.',

  // The tournament pot, end to end on devnet.
  'changelog.torneio-com-pote.nome': 'Tournament with a pot',
  'changelog.torneio-com-pote.resumo':
    'Tournaments can charge an entry fee, with the money held on chain and the champion taking the pot — and so can a best of 3.',
  'changelog.torneio-com-pote.mudanca.0.o':
    'When you open a tournament you can set the entry fee. Each player pays once, and the champion takes the pot.',
  'changelog.torneio-com-pote.mudanca.0.porque':
    'The money sits in a vault on chain, not on the server: who paid is proven on chain, and the server only checks.',
  'changelog.torneio-com-pote.mudanca.1.o':
    'The screen shows the pot for the number of seats before you sign, and your payment receipt afterwards.',
  'changelog.torneio-com-pote.mudanca.1.porque':
    'The receipt used to appear only in the server log — whoever paid had nothing to show.',
  'changelog.torneio-com-pote.mudanca.2.o':
    'A best of 3 can also charge an entry fee, through the same tournament vault.',
  'changelog.torneio-com-pote.mudanca.2.porque':
    'A two-seat series is a two-seat tournament: same vault, same entry, same settlement, with no new on-chain instruction.',
  'changelog.torneio-com-pote.mudanca.3.o':
    'Before you sign, the screen says that opening the room pays rent for the tournament account, and that the rent does not come back.',
  'changelog.torneio-com-pote.mudanca.3.porque':
    'It is your money, and it returns neither on settlement nor on cancellation. Saying so beats hiding it.',

  // The upright table on phones.
  'changelog.mesa-em-pe-no-celular.nome': 'Upright table on phones',
  'changelog.mesa-em-pe-no-celular.resumo':
    'On a phone held upright, the table turns and fills the screen — no need to rotate the device.',
  'changelog.mesa-em-pe-no-celular.mudanca.0.o':
    'The table shows upright when the phone is upright, using the whole screen.',
  'changelog.mesa-em-pe-no-celular.mudanca.0.porque':
    'Lying down on an upright screen, the table was small in the middle with black bands above and below.',
  'changelog.mesa-em-pe-no-celular.mudanca.1.o':
    'With the table upright, your finger grabs the ball where you see it, and the ball numbers stay upright.',
  'changelog.mesa-em-pe-no-celular.mudanca.1.porque':
    'The touch was converted without the rotation: the ball responded to a point turned 90° from what the player saw.',

  'changelog.quebrar-de-novo-vale-quebra.nome': 'Breaking again counts as a break',
  'changelog.quebrar-de-novo-vale-quebra.resumo':
    'When the table gets re-racked, the new break uses the cue’s break bonus and the full power gauge, same as the first one.',
  'changelog.quebrar-de-novo-vale-quebra.mudanca.0.o':
    'Breaking again now counts as a real break: the cue’s break bonus applies, and the power gauge goes all the way.',
  'changelog.quebrar-de-novo-vale-quebra.mudanca.0.porque':
    'Before, a repeat break came out weaker — no cue bonus, and a capped gauge — which favoured whoever forced the re-rack.',

  // The aim: the bug one player described as "the rebound never goes the right
  // way", and both of its causes.
  // Per-table aim mode: one number, and the three modes are presets of it.
  'changelog.mira-rapida-e-relogio-no-meio.nome': 'Quick aim and a clock in the middle',
  'changelog.mira-rapida-e-relogio-no-meio.resumo':
    'One click points the aim, the shot clock stays in sight, and the cloth stays clear for looking at the balls.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.0.o':
    'Clicking a point on the cloth swings the aim to it. Dragging works as before.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.0.porque':
    'The click gets you close, and Shift handles the fine tuning.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.1.o':
    'The shot clock sits large in the middle of the scoreboard and changes colour: green, yellow from 20 s and red from 10 s. Below it, the table pot.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.1.porque':
    'Up at the top, time running out went unnoticed.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.2.o':
    'The pocket prompt moved off the middle of the cloth and onto the scoreboard, in gold. The pockets still blink.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.2.porque':
    'The prompt on the cloth covered exactly the balls you need to see to pick a pocket.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.3.o':
    'The rail shows the balls in the order they dropped.',
  'changelog.mira-rapida-e-relogio-no-meio.mudanca.3.porque':
    'Like a real ball return, the row tells the story of the match.',
  'changelog.a-mesa-que-se-explica.nome': 'A table that explains itself',
  'changelog.a-mesa-que-se-explica.resumo':
    'Your opponent\'s stats one tap away, a match ending that tells you what to do next, and tidier menus.',
  'changelog.a-mesa-que-se-explica.mudanca.0.o':
    'At an online table, tapping a player\'s card on the scoreboard shows their win rate, recent matches and weekly rank.',
  'changelog.a-mesa-que-se-explica.mudanca.0.porque':
    'You can tell whether your opponent is good without leaving the table. The card pauses nothing and closes on its own after 10 seconds.',
  'changelog.a-mesa-que-se-explica.mudanca.1.o':
    'At the end of a match, "Back to the lobby" is now the card\'s main button.',
  'changelog.a-mesa-que-se-explica.mudanca.1.porque':
    'It used to be a grey link in the middle of the card and went unnoticed.',
  'changelog.a-mesa-que-se-explica.mudanca.2.o':
    'The practice menu is now laid out in titled blocks: game mode, new game, language and how to shoot.',
  'changelog.a-mesa-que-se-explica.mudanca.3.o':
    'The chat no longer leaves a gap between messages.',
  'changelog.a-mesa-que-se-explica.mudanca.4.o':
    'Pressing Shift mid-drag no longer pulls the aim or the power back.',
  'changelog.a-mesa-que-se-explica.mudanca.4.porque':
    'Fine adjustment used to rescale the whole drag so far at the new speed, and the aim jumped back about 30°. Now it only applies from that point on.',
  'changelog.disputa-da-saida.nome': 'The lag for the break',
  'changelog.disputa-da-saida.resumo':
    'Who breaks is now decided on the table, in a precision mini game before every match.',
  'changelog.disputa-da-saida.mudanca.0.o':
    'Before the break, both players shoot the same position: whoever leaves the colored ball closest to the center of the target breaks.',
  'changelog.disputa-da-saida.mudanca.0.porque':
    'Until now the player who created the room always broke. The break is an advantage, and now it is earned.',
  'changelog.disputa-da-saida.mudanca.1.o':
    'The position of the balls and the target changes every match, and nobody picks it: it comes from the same two-player draw that already set up the break.',
  'changelog.disputa-da-saida.mudanca.1.porque':
    'With a fixed position, memorizing one shot would be enough. And with the two-player draw, neither player can steer the position their way.',
  'changelog.disputa-da-saida.mudanca.2.o':
    'A tie replays the lag with the order reversed, up to three rounds. Whoever does not shoot within 60 seconds loses the lag.',
  'changelog.disputa-da-saida.mudanca.2.porque':
    'The physics is exact: the same shot always ends in the same place, so the second player could copy the first. Reversing the order takes the point out of copying.',
  'changelog.a-cacapa-em-toda-tacada.nome': 'Call the pocket on every shot',
  'changelog.a-cacapa-em-toda-tacada.resumo':
    'In 8-Ball, after the break, you call the pocket before every shot.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.0.o':
    'After the break, every shot starts by picking a pocket: all six pulse until you tap one, and the cue shows up right after.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.0.porque':
    'No more slamming at random hoping something drops: what counts is what you called.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.1.o':
    'If one of your balls drops in the called pocket, the shot counts, and so do any others that drop with it. If none drops there, your turn ends, with no foul.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.1.porque':
    'Missing the pocket is a missed shot, not a foul: your opponent gets the turn, not ball in hand. On an open table, your group is that of the ball that dropped in the called pocket.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.2.o':
    'Matches recorded earlier still play back with the rules they were played under.',
  'changelog.a-cacapa-em-toda-tacada.mudanca.2.porque':
    'Every replay stores the version it was recorded with, and the version says which rules to replay it with.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.nome': 'Fine power and a cue ball that follows you',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.resumo':
    'The power bar no longer jumps block by block, the cue ball in hand follows the pointer, and the table got a new finish.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.0.o':
    'Any point on the power bar counts. The 10 blocks stay as marks, and the number below becomes a percentage.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.0.porque':
    'With 10 levels there was no power that stopped the cue ball 80 cm away. Each mark is still worth exactly what its level was; now everything between them exists too.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.1.o':
    'With ball in hand after a foul, the cue ball follows the pointer and a click places it. Your opponent sees it moving.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.1.porque':
    'When the cue ball went back to the head spot, you could only move it by clicking right on it. Clicking anywhere else became aiming, and the clock kept running.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.2.o':
    'The pockets got cloth-lined mouths and a crisp rim, and the diamonds moved to the middle of the rail, in ivory.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.3.o':
    'If the match does not start within 2 minutes, the table shows why, how long until the automatic refund, and a button back to the lobby.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.3.porque':
    'Before, the wait just spun silently for up to an hour, with both deposits sitting in the contract.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.4.o':
    'In the lobby, your table shows how long until the deposits come back on their own if the match does not start.',
  'changelog.forca-fina-e-bola-na-mao-que-acompanha.mudanca.4.porque':
    'So nobody is left thinking they lost their entry.',
  'changelog.cada-mesa-escolhe-a-mira.nome': 'Every table picks its own aim',
  'changelog.cada-mesa-escolhe-a-mira.resumo':
    'Whoever creates the table decides how much guide line it shows — from none at all to the full path.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.0.o':
    'Creating a table now lets you pick the aim: none, normal or extended.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.0.porque':
    'With no aim the screen shows nothing about the shot, not even where the cue ball will touch. Normal is the table you already know. Extended shows the whole path with the cushions, so you can see whether the ball drops before you shoot. Players who prefer going by eye no longer have to pretend the line is not there, and anyone learning can turn all the help on.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.1.o':
    'The choice applies to BOTH players, and it shows in the lobby before you deposit.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.1.porque':
    'Seeing the ball travel to the pocket is information your opponent does not have — on a table with SOL at stake, that cannot be one player’s call. The mode belongs to the table, is fixed once created, and the listing shows which one it is so you know what you are joining.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.2.o':
    'You can also type any reach you like, in metres.',
  'changelog.cada-mesa-escolhe-a-mira.mudanca.2.porque':
    'The three modes are shortcuts for a single number: how much of the path after contact the screen draws. If you want something in between — half a metre, say — type the value and the table is created that way.',
  'changelog.a-mira-que-nao-mentia-mais.nome': 'The aim that no longer lies',
  'changelog.a-mira-que-nao-mentia-mais.resumo':
    'The shot goes where the line points, aiming got genuinely fine, and it stopped being a cue attribute.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.0.o':
    'Shots now leave in exactly the direction the guide line shows.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.0.porque':
    'The cue could only point in half-degree steps, and the on-screen line was drawn without that rounding. You aimed at the pocket, the screen agreed, and the ball left up to a quarter of a degree off — which on a long shot is wider than the pocket mouth. There were positions where NO available direction could score, however many times you tried.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.1.o':
    'Aiming turns slowly, and at the same rate anywhere on screen.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.1.porque':
    'Sensitivity depended on how far your finger was from the cue: close in, each pixel was worth over a degree; far out, a tenth of that. The same small move did very different things. The step is now constant, and it gets finer automatically on long shots — you can stop inside the window that sinks the ball. Fine adjust is still on Shift and on the button, for when you want even more.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.2.o':
    'Aim is no longer a cue attribute: every cue points alike.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.2.porque':
    'Precision was the one thing a bought cue gave that no amount of skill could make up for — and it was the broken part. Playing with the free cue now aims exactly like a minted legendary. What cues still differ in is weight, spin, grip and break.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.3.o':
    'Cues were rebalanced around four attributes, and aim levels already bought became grip.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.3.porque':
    'Each rarity budget was the sum of five attributes; without aim they were re-anchored on the sum of the four that remain, keeping the default cue exactly where it was. Nobody loses what they paid for: every aim level bought became a grip level, because the price curve is per attribute and dropping them would make you pay twice.',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.4.o':
    'The "Precise" cue is now called "Control".',
  'changelog.a-mira-que-nao-mentia-mais.mudanca.4.porque':
    'Its identity was having the finest aim in the collection, and that no longer exists. It became the cue that holds the cue ball where you left it: the highest grip in the catalogue. Same item, same history — only the name and the numbers changed.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.nome':
    'Table sound, and power back in range',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.resumo':
    'Balls now sound while they travel, and the hardest shot is a shot again.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.0.o':
    'You can hear the balls rolling and scraping on the cloth, and the sound changes with speed.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.0.porque':
    'Rolling and sliding sound different, and the physics already told them apart: a freshly struck ball slides until friction lines up its spin, and only then rolls. On a break the sound starts as a scrape and turns into a rumble.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.1.o':
    'Full power on a normal shot is no longer harder than a professional break.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.1.porque':
    'Full power meant 26.8 mph on any shot — above the hardest break measured in competition. With draw, the cue ball came back at 20 mph, crossed the table and bounced off the rail. The break still uses the top of the range; nothing else does.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.2.o':
    'On the break, you can place the cue ball right on the head string.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.2.porque':
    'The screen stopped the ball one radius short of the line, as if it were a rail, while the server always judged by the centre. That erased 6% of the kitchen — and precisely the strip closest to the rack, which is the useful one.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.3.o':
    'Using the power bar no longer locks your aim from then on.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.3.porque':
    'After dragging the bar once, every following drag still counted as power, and aiming stopped working until you reloaded the page.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.4.o':
    'After the first shot, aiming works again.',
  'changelog.o-som-da-mesa-e-a-forca-no-lugar.mudanca.4.porque':
    'The pointer stayed locked with nobody dragging, and from then on every touch seemed to start in the same place — near the cue ball, which means always on the cue. The game looked like it had crashed.',
  'changelog.quebra-com-a-branca-na-mao.nome': 'Break with ball in hand',
  'changelog.quebra-com-a-branca-na-mao.resumo':
    'The break now follows the WPA rule: whoever breaks chooses where to place the cue ball.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.0.o':
    'On the break, drag the cue ball anywhere behind the head string.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.0.porque':
    "It is the WPA rule, and the break is the shot that depends most on where it starts — " +
    'forcing everyone to start from the same spot impoverishes exactly the shot where both ' +
    'sides have the same chance.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.1.o':
    'Anyone who does not want to move the cue ball breaks from the kitchen spot, as before.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.1.porque':
    'Moving it is a right, not a step: nobody needs an extra tap per rack.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.2.o':
    'Matches recorded before this version keep proving exactly as they did before.',
  'changelog.quebra-com-a-branca-na-mao.mudanca.2.porque':
    'An old replay is played back with the rules from when it was played. Without that, ' +
    'changing a rule would erase the past — and there are matches settled on-chain that depend ' +
    'on staying verifiable.',

  'changelog.mirar-e-tacar-separados.nome': 'Aiming and shooting apart',
  'changelog.mirar-e-tacar-separados.resumo':
    'Two gestures instead of one, and the old mode stays available.',
  'changelog.mirar-e-tacar-separados.mudanca.0.o':
    'Drag the cloth to aim; grab the cue and pull to shoot.',
  'changelog.mirar-e-tacar-separados.mudanca.0.porque':
    'On phones, a single drag that both aimed and shot meant checking your aim with a finger ' +
    'could send the ball and cost the turn.',
  'changelog.mirar-e-tacar-separados.mudanca.1.o':
    'The power bar also picks the power: drag it and let go.',
  'changelog.mirar-e-tacar-separados.mudanca.1.porque':
    'It is what players already tried on their own — a bar on screen invites the gesture.',
  'changelog.mirar-e-tacar-separados.mudanca.2.o':
    'The old mode — one drag does everything — stays in the menu, under "How to shoot".',
  'changelog.mirar-e-tacar-separados.mudanca.2.porque':
    'It is faster with a mouse, and it is what returning players already have in their hand. ' +
    'The default comes from the device: touch gets the two gestures, mouse gets the single drag.',
  'changelog.mirar-e-tacar-separados.mudanca.3.o':
    'The prediction line shows up for the whole turn, not just with the cue pulled back.',
  'changelog.mirar-e-tacar-separados.mudanca.3.porque':
    'With aim and power split apart, it used to vanish right when choosing where to aim.',

  'changelog.a-tela-e-a-mesa.nome': 'The screen is the table',
  'changelog.a-tela-e-a-mesa.resumo':
    'The header and the panel are out of the way; the cloth fills the whole window.',
  'changelog.a-tela-e-a-mesa.mudanca.0.o':
    'Buttons in the corners, over the cloth: menu and chat on the left, shot on the right.',
  'changelog.a-tela-e-a-mesa.mudanca.0.porque':
    'On a pool table, screen space is aiming precision.',
  'changelog.a-tela-e-a-mesa.mudanca.1.o':
    'The power bar moved to the right, on the side of the shooting hand.',
  'changelog.a-tela-e-a-mesa.mudanca.2.o':
    'The spin control became a button: the cue ball at the top shows the chosen spin and opens large.',
  'changelog.a-tela-e-a-mesa.mudanca.2.porque':
    'It used to take up a fixed strip on the side for a choice that does not change every shot.',
  'changelog.a-tela-e-a-mesa.mudanca.3.o':
    'On wide screens, chat sits beside the table instead of covering it.',
  'changelog.a-tela-e-a-mesa.mudanca.4.o':
    'On phones, the "Rotate screen" button tries to rotate the device and, where the browser ' +
    'will not allow it, rotates the layout instead.',
  'changelog.a-tela-e-a-mesa.mudanca.4.porque':
    "There is no rotation API in wallets' built-in browser or in Safari on iPhone — and that is " +
    'where this game is opened most often.',
  'changelog.a-tela-e-a-mesa.mudanca.5.o':
    'The cue ball no longer has red dots: they are light gray now.',
  'changelog.a-tela-e-a-mesa.mudanca.5.porque':
    'The question was "why isn\'t the cue ball white?". It cannot be plain white, or the applied ' +
    'spin would be invisible.',

  'changelog.compat.titulo': 'Old matches still hold',
  'changelog.compat.intro':
    "Changing a rule can't erase the past: a replay is played back with the rules from when " +
    'it was played.',
  'changelog.compat.texto': {
    one: 'Today the game <strong>records</strong> in format <span class="tabular-nums">{grava}</span> and <strong>reads</strong> format <span class="tabular-nums">{le}</span>.',
    other:
      'Today the game <strong>records</strong> in format <span class="tabular-nums">{grava}</span> and <strong>reads</strong> formats <span class="tabular-nums">{le}</span>.',
  },
  'changelog.compat.rodape': 'These two numbers come from the code itself, not from this text.',
}
